<template>
  <div>
    <div v-if="isMaintenanceMode" class="maintenance-overlay">
      <div class="maintenance-modal">
        <h2>{{ $t('dashboard.maintenance.title') }}</h2>
        <p>
          {{ $t('dashboard.maintenance.line1') }}
          <br>
          {{ $t('dashboard.maintenance.line2') }}
        </p>
        <p>{{ $t('dashboard.maintenance.thanks') }}</p>
        <p>{{ $t('dashboard.maintenance.estimatedTime') }}</p>
      </div>
    </div>

    <div class="content">
      <DeleteConfirmationModal
        v-if="showDeleteModal"
        :dossierName="dossierToDelete?.name"
        @confirm="confirmDeleteDossier"
        @cancel="cancelDeleteDossier"
      />
      <DeleteGroupModal
        v-if="groupToDelete"
        :groupName="groupToDelete.name"
        :itemCount="groupToDelete.dossiers.length"
        @confirm="confirmDeleteGroup"
        @cancel="cancelDeleteGroup"
      />
      <RenameDossierModal
        v-if="showRenameModal"
        :original-name="itemToRename.name"
        @confirm="confirmRename"
        @cancel="cancelRename"
      />
      <CreateGroupModal
        v-if="showCreateGroupModal"
        @confirm="confirmCreateGroup"
        @cancel="closeCreateGroupModal"
      />
      <WorkspaceCreateModal
        v-if="showCreateWorkspaceModal"
        @workspace-created="onWorkspaceCreated"
        @ingest-started="onIngestStarted"
        @close="onCreateWorkspaceClosed"
      />
      <WorkspaceManageModal
        v-if="showManageWorkspaceModal"
        :initial-workspace-id="manageWorkspaceInitialId"
        @workspace-created="onWorkspaceCreated"
        @workspace-deleted="onWorkspaceDeleted"
        @workspace-selected="onWorkspaceSelectedFromModal"
        @close="showManageWorkspaceModal = false; manageWorkspaceInitialId = null"
      />
      <WorkspaceReorganizeModal
        :workspace="reorgWorkspace"
        @close="reorgWorkspace = null"
        @created="onReorganizeCreated"
      />
      <!-- Rédacteur : gestion des modèles (ouvert via le bouton « + » → Rédiger) -->
      <TemplateManagerModal
        v-if="showTemplateManager"
        @close="showTemplateManager = false"
        @use="onTemplateUse"
      />
      <SubscriptionModal
        :show="showPaywall"
        @close="closePaywallModal"
        @select-plan="upgrade"
        @navigate-to-trial="navigateToTrial"
        @navigate-to-contact="navigateToContact"
        @navigate-to-student="navigateToStudent"
      />

      <div v-if="showInfoModal" class="info-modal-overlay" @click="closeInfoModal">
        <div class="info-modal" @click.stop>
          <div class="info-modal-header">
            <h3>{{ $t('dashboard.infoModal.title') }}</h3>
            <button class="info-modal-close" @click="closeInfoModal">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8z"/>
              </svg>
            </button>
          </div>
          <div class="info-modal-body">
            <p>{{ selectedDossierInfo }}</p>
          </div>
          <div class="info-modal-footer">
            <button class="btn button-base tech-purple" @click="closeInfoModal">
              {{ $t('common.close') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Tableau de Jurisprudence Modal -->
      <div v-if="showTableauModal" class="info-modal-overlay" @click="closeTableauModal">
        <div class="tableau-modal" @click.stop>
          <div class="info-modal-header">
            <button class="info-modal-close" @click="closeTableauModal">&times;</button>
          </div>
          <div class="tableau-modal-body">
            <div v-if="tableauSubQuestionsLoading" class="tableau-loading-box">
              <div class="tableau-loading-spinner"></div>
              <p>{{ $t('dashboard.tableau.identifyingSubQuestions') }}</p>
            </div>
            <template v-else>
              <p class="tableau-modal-hint">{{ $t('dashboard.tableau.modalHint', { max: tableauSubQuestionsMax }) }}</p>
              <div
                v-for="(sq, idx) in tableauSubQuestions"
                :key="idx"
                class="tableau-sq-row"
              >
                <span class="tableau-sq-label">{{ idx + 1 }}.</span>
                <textarea
                  v-model="tableauSubQuestions[idx]"
                  class="tableau-sq-input"
                  :placeholder="$t('dashboard.tableau.subQuestionPlaceholder')"
                  rows="2"
                  @input="autoResizeTextarea($event)"
                />
                <button
                  v-if="tableauSubQuestions.length > 1"
                  class="tableau-sq-remove"
                  @click="tableauSubQuestions.splice(idx, 1)"
                >&times;</button>
              </div>
              <button
                class="btn tableau-add-btn"
                @click="tableauSubQuestions.push('')"
                :disabled="tableauSubQuestions.length >= tableauSubQuestionsMax"
              >
                {{ $t('dashboard.tableau.addColumn') }}
              </button>
            </template>
          </div>
          <div class="info-modal-footer" style="display:flex; gap:10px; justify-content:flex-end;">
            <button class="btn button-base" style="font-size:12px;" @click="closeTableauModal">{{ $t('common.cancel') }}</button>
            <button
              class="btn button-base tech-purple"
              style="font-size:12px;"
              @click="handleTableauQuery"
              :disabled="tableauSubQuestionsLoading || isSubmittingTableau || tableauSubQuestions.filter(s => s.trim()).length === 0"
            >
              {{ isSubmittingTableau ? $t('dashboard.common.sending') : $t('dashboard.tableau.launch') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Improve Question Modal (baguette magique) -->
      <transition name="improve-fade">
        <div v-if="showImproveModal" class="improve-modal-overlay" @click.self="closeImproveModal">
          <div class="improve-modal" @click.stop>
            <!-- Header fixe -->
            <div class="improve-modal-header">
              <div class="improve-modal-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M9.5 2.672a.5.5 0 1 0 1 0V.843a.5.5 0 0 0-1 0v1.829Zm4.5.035A.5.5 0 0 0 13.293 2L12 3.293a.5.5 0 1 0 .707.707L14 2.707ZM7.293 4A.5.5 0 1 0 8 3.293L6.707 2A.5.5 0 0 0 6 2.707L7.293 4Zm-.621 2.5a.5.5 0 1 0 0-1H4.843a.5.5 0 1 0 0 1h1.829Zm8.485 0a.5.5 0 1 0 0-1h-1.829a.5.5 0 0 0 0 1h1.829ZM13.293 10A.5.5 0 1 0 14 9.293L12.707 8a.5.5 0 1 0-.707.707L13.293 10ZM9.5 11.157a.5.5 0 0 0 1 0V9.328a.5.5 0 0 0-1 0v1.829Zm1.854-5.097a.5.5 0 0 0 0-.706l-.708-.708a.5.5 0 0 0-.707 0L8.646 5.94a.5.5 0 0 0 0 .707l.708.708a.5.5 0 0 0 .707 0l1.293-1.293Zm-3 3a.5.5 0 0 0 0-.706l-.708-.708a.5.5 0 0 0-.707 0L.646 13.94a.5.5 0 0 0 0 .707l.708.708a.5.5 0 0 0 .707 0L8.354 9.06Z"/>
                </svg>
              </div>
              <div>
                <h3 class="improve-modal-title">{{ $t('dashboard.improve.title') }}</h3>
                <p class="improve-modal-subtitle">
                  {{ (improveStep === 'result' || improveStep === 'rewriting') ? $t('dashboard.improve.subtitleResult') : $t('dashboard.improve.subtitleQuestions') }}
                </p>
              </div>
              <button class="improve-modal-close" @click="closeImproveModal">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
                </svg>
              </button>
            </div>

            <!-- Corps : seule zone scrollable -->
            <div class="improve-modal-body">
              <div v-if="improveStep === 'loading' || improveStep === 'rewriting'" class="improve-waiting">
                <div class="improve-waiting-dots"><span></span><span></span><span></span></div>
                <p class="improve-waiting-text">
                  {{ improveStep === 'loading' ? $t('dashboard.improve.analyzing') : $t('dashboard.improve.rewriting') }}
                </p>
              </div>

              <template v-else-if="improveStep === 'questions'">
                <div
                  v-for="(q, idx) in improveQuestions"
                  :key="idx"
                  class="improve-q-card"
                  :class="{ 'improve-q-card--answered': (improveAnswers[idx] || '').trim() }"
                >
                  <p class="improve-q-text">{{ q }}</p>
                  <textarea
                    :value="improveAnswers[idx]"
                    class="improve-a-input"
                    :placeholder="$t('dashboard.improve.answerPlaceholder')"
                    rows="1"
                    @input="onImproveAnswerInput($event, idx)"
                    @keydown.enter.exact.prevent="submitImproveAnswers"
                  />
                </div>
              </template>

              <template v-else-if="improveStep === 'result'">
                <div class="improve-original">
                  <span class="improve-block-label">{{ $t('dashboard.improve.originalLabel') }}</span>
                  <p class="improve-original-text">{{ improveOriginalQuestion }}</p>
                </div>
                <div>
                  <span class="improve-block-label improve-block-label--accent">{{ $t('dashboard.improve.improvedLabel') }}</span>
                  <textarea
                    v-model="improvedQuestion"
                    class="improve-result-input improve-reveal"
                    rows="6"
                    @input="autoResizeTextarea($event)"
                  />
                </div>
              </template>
            </div>

            <!-- Footer fixe : toujours visible -->
            <div v-if="improveStep === 'questions' || improveStep === 'result'" class="improve-modal-footer">
              <span v-if="improveStep === 'questions'" class="improve-footer-note">{{ $t('dashboard.improve.optionalNote') }}</span>
              <div class="improve-footer-actions">
                <button class="improve-btn-ghost" @click="closeImproveModal">{{ $t('common.cancel') }}</button>
                <button
                  v-if="improveStep === 'questions'"
                  class="improve-btn-primary"
                  @click="submitImproveAnswers"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M9.5 2.672a.5.5 0 1 0 1 0V.843a.5.5 0 0 0-1 0v1.829Zm4.5.035A.5.5 0 0 0 13.293 2L12 3.293a.5.5 0 1 0 .707.707L14 2.707ZM7.293 4A.5.5 0 1 0 8 3.293L6.707 2A.5.5 0 0 0 6 2.707L7.293 4Zm-.621 2.5a.5.5 0 1 0 0-1H4.843a.5.5 0 1 0 0 1h1.829Zm8.485 0a.5.5 0 1 0 0-1h-1.829a.5.5 0 0 0 0 1h1.829ZM13.293 10A.5.5 0 1 0 14 9.293L12.707 8a.5.5 0 1 0-.707.707L13.293 10ZM9.5 11.157a.5.5 0 0 0 1 0V9.328a.5.5 0 0 0-1 0v1.829Zm1.854-5.097a.5.5 0 0 0 0-.706l-.708-.708a.5.5 0 0 0-.707 0L8.646 5.94a.5.5 0 0 0 0 .707l.708.708a.5.5 0 0 0 .707 0l1.293-1.293Zm-3 3a.5.5 0 0 0 0-.706l-.708-.708a.5.5 0 0 0-.707 0L.646 13.94a.5.5 0 0 0 0 .707l.708.708a.5.5 0 0 0 .707 0L8.354 9.06Z"/>
                  </svg>
                  {{ $t('dashboard.improve.improveButton') }}
                </button>
                <button
                  v-if="improveStep === 'result'"
                  class="improve-btn-primary"
                  :disabled="!improvedQuestion.trim()"
                  @click="applyImprovedQuestion"
                >
                  {{ $t('dashboard.improve.useButton') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </transition>

      <div class="search-section text-center">
        <div v-if="showQuotaExceededModal" class="quota-overlay">
          <div class="quota-modal">
            <h2>{{ $t('dashboard.quota.title') }}</h2>
            <p>{{ $t('dashboard.quota.message', { date: formattedCurrentPeriodEnd }) }}</p>
            <button class="btn button-base tech-purple" @click="closeQuotaExceededModal">
              {{ $t('common.ok') }}
            </button>
          </div>
        </div>
        <h2 class="search-title highlight">{{ $t('dashboard.search.title') }}</h2>

        <div class="main-section">
          <div class="search-bar mx-auto mt-4">
            <!-- Chip du modèle de rédaction sélectionné : l'envoi suit le flux
                 dossier agent normal avec template_id (le chip implique l'agent) -->
            <div v-if="selectedTemplateChip" class="redacteur-chip-row">
              <span class="redacteur-chip" :title="$t('dashboard.redacteurChip.tooltip')">
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 16 16" fill="none">
                  <path d="M3 2h6l4 4v8a1 1 0 01-1 1H3a1 1 0 01-1-1V3a1 1 0 011-1z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
                  <path d="M9 2v4h4" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
                </svg>
                <span class="redacteur-chip-name">{{ selectedTemplateChip.name }}</span>
                <button
                  class="redacteur-chip-remove"
                  @click="clearTemplateChip"
                  :title="$t('dashboard.redacteurChip.remove')"
                  :aria-label="$t('dashboard.redacteurChip.remove')"
                >
                  <svg width="10" height="10" viewBox="0 0 16 16" fill="none"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
                </button>
              </span>
            </div>
            <div class="input-container">
              <textarea
                v-model="searchQuery"
                class="form-control search-input"
                :placeholder="$t('dashboard.search.placeholder')"
                rows="1"
                ref="searchBox"
                @input="adjustTextareaHeight"
                @keydown.enter.exact.prevent="handleSearchSubmit"
              ></textarea>
            </div>
            <div class="buttons-line" v-if="!isSearchActive">
              <!-- Left buttons group (+ menu + Mode) -->
              <div class="left-buttons-group">
                <!-- Bouton « + » : popover Sources / Rédiger -->
                <div class="sources-dropdown-container">
                  <button
                    class="btn plus-button"
                    :class="{ 'active': showPlusMenu || showSourcesDropdown }"
                    @click.stop="togglePlusMenu"
                    :title="$t('dashboard.plusMenu.tooltip')"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round">
                      <path d="M8 3v10M3 8h10" />
                    </svg>
                  </button>

                  <!-- Popover du bouton « + » -->
                  <div v-if="showPlusMenu" class="plus-menu" @click.stop>
                    <button
                      class="plus-menu-option"
                      :disabled="selectedQueryMode === 'TABLEAU' || selectedWorkspaceId !== null"
                      :title="selectedWorkspaceId !== null ? 'Workspace sélectionné — utilisez le bouton Agent' : (selectedQueryMode === 'TABLEAU' ? $t('dashboard.sources.tableauAuto') : $t('dashboard.sources.select'))"
                      @click="openSourcesFromPlusMenu"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M12.5 16a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7m.5-5v1h1a.5.5 0 0 1 0 1h-1v1a.5.5 0 0 1-1 0v-1h-1a.5.5 0 0 1 0-1h1v-1a.5.5 0 0 1 1 0"/>
                        <path d="M2 1a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v6.5a.5.5 0 0 1-1 0V1H3v14h3a.5.5 0 0 1 0 1H3a1 1 0 0 1-1-1z"/>
                        <path d="M4.5 3a.5.5 0 0 0 0 1h7a.5.5 0 0 0 0-1zm0 2a.5.5 0 0 0 0 1h7a.5.5 0 0 0 0-1zm0 2a.5.5 0 0 0 0 1h4a.5.5 0 0 0 0-1z"/>
                      </svg>
                      <span>{{ $t('dashboard.sources.label') }}</span>
                      <span v-if="selectedSourcesCount < availableSources.length" class="sources-badge">
                        {{ selectedSourcesCount }}
                      </span>
                    </button>
                    <button v-if="canUseRedacteur" class="plus-menu-option" @click="openTemplateManagerFromPlusMenu">
                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 16 16" fill="none">
                        <path d="M11 2l3 3-9 9H2v-3l9-9z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
                      </svg>
                      <span>{{ $t('dashboard.plusMenu.write') }}</span>
                    </button>
                  </div>

                  <!-- Sources Dropdown -->
                  <div v-if="showSourcesDropdown" class="sources-dropdown" @click.stop>
                    <div class="sources-dropdown-header">
                      <span>{{ $t('dashboard.sources.select') }}</span>
                      <div class="sources-actions">
                        <button @click="selectAllSources" class="sources-action-btn">{{ $t('dashboard.sources.selectAll') }}</button>
                        <button @click="deselectAllSources" class="sources-action-btn">{{ $t('dashboard.sources.selectNone') }}</button>
                      </div>
                    </div>

                    <div class="sources-dropdown-body">
                      <div class="sources-category">
                        <div class="sources-category-title">{{ $t('dashboard.sources.primary') }}</div>
                        <label
                          v-for="source in principalSources"
                          :key="source.id"
                          class="source-item"
                        >
                          <input
                            type="checkbox"
                            v-model="source.selected"
                            class="source-checkbox"
                          />
                          <span class="source-label">{{ source.title }}</span>
                        </label>
                      </div>

                      <div class="sources-category">
                        <div class="sources-category-title">{{ $t('dashboard.sources.secondary') }}</div>
                        <label
                          v-for="source in secondarySources"
                          :key="source.id"
                          class="source-item"
                        >
                          <input
                            type="checkbox"
                            v-model="source.selected"
                            class="source-checkbox"
                          />
                          <span class="source-label">{{ source.title }}</span>
                        </label>
                      </div>
                    </div>

                    <div class="sources-dropdown-footer">
                      <button @click="showSourcesDropdown = false" class="btn button-base tech-purple" style="font-size: 11px; padding: 6px 16px;">
                        {{ $t('common.close') }}
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Workspace selector — exposé seulement pour le mode WORKSPACE -->
                <div v-if="isWorkspaceUser" class="workspace-selector-container">
                  <button
                    class="btn sources-button"
                    :class="{ 'active': showWorkspaceDropdown, 'has-selection': selectedWorkspaceId !== null }"
                    @click.stop="toggleWorkspaceDropdown"
                    title="Workspace — documents uploadés par toi"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M9.828 3h3.982a2 2 0 0 1 1.992 2.181l-.637 7A2 2 0 0 1 13.174 14H2.826a2 2 0 0 1-1.991-1.819l-.637-7a2 2 0 0 1 .342-1.31L.5 3a2 2 0 0 1 2-2h3.672a2 2 0 0 1 1.414.586l.828.828A2 2 0 0 0 9.828 3m-8.322.12q.322-.119.684-.12h5.396l-.707-.707A1 1 0 0 0 6.172 2H2.5a1 1 0 0 0-1 .981z"/>
                    </svg>
                    <span>{{ selectedWorkspaceId ? selectedWorkspaceName() : 'Workspace' }}<template v-if="selectedWorkspaceId && selectedWorkspaceScope && selectedWorkspaceScope.length"> · {{ selectedWorkspaceScope.length }} fichier{{ selectedWorkspaceScope.length > 1 ? 's' : '' }}</template></span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="currentColor" viewBox="0 0 16 16">
                      <path fill-rule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
                    </svg>
                  </button>
                  <div v-if="showWorkspaceDropdown" class="workspace-dropdown" @click.stop>
                    <div class="workspace-dropdown-header">
                      <span>Choisir un workspace</span>
                    </div>
                    <div class="workspace-dropdown-body">
                      <button
                        class="workspace-item"
                        :class="{ 'workspace-item--active': selectedWorkspaceId === null }"
                        @click="selectWorkspace(null)"
                      >
                        <span>Aucun (sources juridiques seules)</span>
                      </button>
                      <div v-for="ws in workspaces.slice(0, 3)" :key="ws.id" class="workspace-row">
                        <button
                          class="workspace-item"
                          :class="{
                            'workspace-item--active': selectedWorkspaceId === ws.id,
                            'workspace-item--pending': ws.status !== 'ready' && ws.status !== 'failed',
                            'workspace-item--failed': ws.status === 'failed'
                          }"
                          :disabled="ws.status !== 'ready'"
                          :title="ws.status === 'failed' ? (ws.ingest_error || 'Ingestion échouée') : ''"
                          @click="selectWorkspace(ws)"
                        >
                          <div class="workspace-item__main">
                            <span class="workspace-item__name">{{ ws.name }}</span>
                          </div>
                          <span v-if="ws.status !== 'ready' && ws.status !== 'failed'" class="workspace-item__spinner" title="Ingestion en cours…">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                              <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
                            </svg>
                          </span>
                          <span v-else-if="ws.status === 'failed'" class="workspace-item__failed" title="Échec">✕</span>
                        </button>
                        <div class="workspace-item__actions">
                          <button
                            v-if="ws.status === 'ready'"
                            class="ws-action-btn"
                            title="Réorganiser avec l'IA"
                            @click.stop="openReorganizeModal(ws)"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="currentColor" viewBox="0 0 16 16">
                              <path d="M9.5 2.672a.5.5 0 1 0 1 0V.843a.5.5 0 0 0-1 0v1.829Zm4.5.035A.5.5 0 0 0 13.293 2L12 3.293a.5.5 0 1 0 .707.707L14 2.707ZM7.293 4A.5.5 0 1 0 8 3.293L6.707 2A.5.5 0 0 0 6 2.707L7.293 4Zm-.621 2.5a.5.5 0 1 0 0-1H4.843a.5.5 0 1 0 0 1h1.829Zm8.485 0a.5.5 0 1 0 0-1h-1.829a.5.5 0 0 0 0 1h1.829ZM13.293 10A.5.5 0 1 0 14 9.293L12.707 8a.5.5 0 1 0-.707.707L13.293 10ZM9.5 11.157a.5.5 0 0 0 1 0V9.328a.5.5 0 0 0-1 0v1.829Zm1.854-5.097a.5.5 0 0 0 0-.706l-.708-.708a.5.5 0 0 0-.707 0L8.646 5.94a.5.5 0 0 0 0 .707l.708.708a.5.5 0 0 0 .707 0l1.293-1.293Zm-3 3a.5.5 0 0 0 0-.706l-.708-.708a.5.5 0 0 0-.707 0L.646 13.94a.5.5 0 0 0 0 .707l.708.708a.5.5 0 0 0 .707 0L8.354 9.06Z"/>
                            </svg>
                          </button>
                          <button
                            v-if="ws.status === 'ready'"
                            class="ws-action-btn"
                            title="Télécharger le workspace (.zip)"
                            @click.stop="downloadWorkspaceArchive(ws)"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                              <path d="M8 2v8M4.5 6.5L8 10l3.5-3.5M2.5 13h11" />
                            </svg>
                          </button>
                          <button
                            class="ws-action-btn"
                            title="Gérer ce workspace"
                            @click.stop="openManageWorkspaceModal(ws.id)"
                          >
                            <img src="@/assets/card-more.svg" alt="Gérer" class="ws-more-icon">
                          </button>
                        </div>
                      </div>
                      <button
                        v-if="workspaces.length > 0"
                        class="workspace-item workspace-item--more"
                        @click="openManageWorkspaceModal"
                      >
                        <span>Plus de workspaces…</span>
                        <span v-if="workspaces.length > 3" class="workspace-more-count">{{ workspaces.length - 3 }}</span>
                      </button>
                      <div v-if="workspaces.length === 0" class="workspace-empty">
                        Aucun workspace
                      </div>
                    </div>
                    <div class="workspace-dropdown-footer">
                      <button class="workspace-create-btn" @click="openCreateWorkspaceModal">
                        + Nouveau workspace
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Mode selector (TABLEAU + WORKSPACE users) — placé à côté de Sources -->
                <div
                  v-if="userModeLoaded && isAgentUser"
                  class="mode-selector-container"
                >
                  <button
                    class="btn sources-button mode-selector-btn mode-active"
                    :class="{ 'active': showModeDropdown }"
                    @click.stop="selectedWorkspaceId === null && toggleModeDropdown()"
                    :disabled="selectedWorkspaceId !== null"
                    :title="selectedWorkspaceId !== null ? 'Workspace sélectionné — utilisez le bouton Agent' : $t('dashboard.modes.chooseMode')"
                  >
                    <span>{{ selectedQueryMode === 'FLASH' ? $t('dashboard.modes.fast') : selectedQueryMode === 'TABLEAU' ? $t('dashboard.modes.tableau') : selectedQueryMode === 'AGENT' ? $t('dashboard.modes.agent') : $t('dashboard.modes.longAnswer') }}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="currentColor" viewBox="0 0 16 16">
                      <path fill-rule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
                    </svg>
                  </button>
                  <div v-if="showModeDropdown" class="mode-dropdown">
                    <button
                      v-if="isFuturUser"
                      class="mode-dropdown-option"
                      :class="{ 'mode-dropdown-option--active': selectedQueryMode === 'AGENT' }"
                      @click="selectedQueryMode = 'AGENT'; showModeDropdown = false"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="#6c63ff" viewBox="0 0 16 16" style="margin-right:6px;flex-shrink:0;">
                        <path d="M5.68 5.792 7.345 7.75 5.68 9.708a2.75 2.75 0 1 1 0-3.916ZM8 6.978 6.416 5.113l-.014-.015a3.75 3.75 0 1 0 0 5.304l.014-.015L8 8.522l1.586 1.865.014.015a3.75 3.75 0 1 0 0-5.304l-.014.015zm.656.772 1.663-1.958a2.75 2.75 0 1 1 0 3.916z"/>
                      </svg>
                      {{ $t('dashboard.modes.agent') }}
                    </button>
                    <button
                      class="mode-dropdown-option"
                      :class="{ 'mode-dropdown-option--active': selectedQueryMode === null }"
                      @click="selectedQueryMode = null; showModeDropdown = false"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="#374151" viewBox="0 0 16 16" style="margin-right:6px;flex-shrink:0;">
                        <path d="M14 1a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H4.414A2 2 0 0 0 3 11.586l-2 2V2a1 1 0 0 1 1-1zM2 0a2 2 0 0 0-2 2v12.793a.5.5 0 0 0 .854.353l2.853-2.853A1 1 0 0 1 4.414 12H14a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2z"/>
                      </svg>
                      {{ $t('dashboard.modes.longAnswer') }}
                    </button>
                    <button
                      class="mode-dropdown-option"
                      :class="{ 'mode-dropdown-option--active': selectedQueryMode === 'FLASH' }"
                      @click="selectedQueryMode = 'FLASH'; showModeDropdown = false"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="#6c63ff" viewBox="0 0 16 16" style="margin-right:6px;flex-shrink:0;">
                        <path d="M11.251.068a.5.5 0 0 1 .227.58L9.677 6.5H13a.5.5 0 0 1 .364.843l-8 8.5a.5.5 0 0 1-.842-.49L6.323 9.5H3a.5.5 0 0 1-.364-.843l8-8.5a.5.5 0 0 1 .615-.09z"/>
                      </svg>
                      {{ $t('dashboard.modes.fastAnswer') }}
                    </button>
                    <button
                      class="mode-dropdown-option"
                      :class="{ 'mode-dropdown-option--active': selectedQueryMode === 'TABLEAU' }"
                      @click="selectedQueryMode = 'TABLEAU'; showModeDropdown = false"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="#6c63ff" viewBox="0 0 16 16" style="margin-right:6px;flex-shrink:0;">
                        <path d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm15 2h-4v3h4zm0 4h-4v3h4zm0 4h-4v3h3a1 1 0 0 0 1-1zm-5 3v-3H6v3zm-5 0v-3H1v2a1 1 0 0 0 1 1zm-4-4h4V8H1zm0-4h4V4H1zm5-3v3h4V4zm4 4H6v3h4z"/>
                      </svg>
                      {{ $t('dashboard.modes.tableau') }}
                    </button>
                  </div>
                </div>
              </div>

              <!-- Action Buttons Group -->
              <div class="action-buttons-group">

                <!-- Bouton Réponse rapide : FLASH ou premium actif non-trial -->
                <button
                  v-if="userModeLoaded && !isAgentUser && (!isTrialing || userMode === 'FLASH')"
                  class="btn button-base search-button flash-button"
                  style="font-size: 12px"
                  @click="handleFlashQuery"
                  :disabled="isSubmittingFlash || selectedSourcesCount === 0 || selectedWorkspaceId !== null"
                  :title="selectedWorkspaceId !== null ? 'Workspace sélectionné — utilisez le bouton Agent' : ''"
                >
                  {{ isSubmittingFlash ? $t('dashboard.common.sending') : $t('dashboard.modes.fastAnswer') }}
                </button>

                <!-- Bouton Agent : pour tous les utilisateurs TABLEAU / WORKSPACE -->
                <!-- (les users FUTUR utilisent le sélecteur de mode + bouton Recherche unique) -->
                <button
                  v-if="isAgentUser && !isFuturUser"
                  class="btn button-base search-button agent-button"
                  style="font-size: 12px; margin-left: 10px;"
                  @click="handleAgentQuery"
                  :disabled="isSubmittingAgent || selectedSourcesCount === 0"
                >
                  {{ isSubmittingAgent ? $t('dashboard.common.sending') : $t('dashboard.modes.agent') }}
                </button>

                <!-- Microphone Button (dictée) — à côté de la baguette -->
                <button
                  class="btn mic-button"
                  style="margin-left: 10px;"
                  :class="{ 'recording': isRecording, 'transcribing': isTranscribing }"
                  @click="toggleRecording"
                  :disabled="isTranscribing"
                  :title="isRecording ? $t('dashboard.recording.stop') : $t('dashboard.recording.start')"
                >
                  <svg v-if="!isRecording && !isTranscribing" xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M3.5 6.5A.5.5 0 0 1 4 7v1a4 4 0 0 0 8 0V7a.5.5 0 0 1 1 0v1a5 5 0 0 1-4.5 4.975V15h3a.5.5 0 0 1 0 1h-7a.5.5 0 0 1 0-1h3v-2.025A5 5 0 0 1 3 8V7a.5.5 0 0 1 .5-.5"/>
                    <path d="M10 8a2 2 0 1 1-4 0V3a2 2 0 1 1 4 0zM8 0a3 3 0 0 0-3 3v5a3 3 0 0 0 6 0V3a3 3 0 0 0-3-3"/>
                  </svg>
                  <!-- Stop icon when recording -->
                  <svg v-else-if="isRecording" xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M5 3.5h6A1.5 1.5 0 0 1 12.5 5v6a1.5 1.5 0 0 1-1.5 1.5H5A1.5 1.5 0 0 1 3.5 11V5A1.5 1.5 0 0 1 5 3.5"/>
                  </svg>
                  <!-- Spinner when transcribing -->
                  <div v-else class="mic-spinner"></div>
                </button>

                <!-- Improve Question (baguette magique) -->
                <button
                  class="btn improve-button"
                  style="margin-left: 10px;"
                  @click="openImproveModal"
                  :disabled="!searchQuery.trim() || showImproveModal"
                  :title="$t('dashboard.improve.tooltip')"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M9.5 2.672a.5.5 0 1 0 1 0V.843a.5.5 0 0 0-1 0v1.829Zm4.5.035A.5.5 0 0 0 13.293 2L12 3.293a.5.5 0 1 0 .707.707L14 2.707ZM7.293 4A.5.5 0 1 0 8 3.293L6.707 2A.5.5 0 0 0 6 2.707L7.293 4Zm-.621 2.5a.5.5 0 1 0 0-1H4.843a.5.5 0 1 0 0 1h1.829Zm8.485 0a.5.5 0 1 0 0-1h-1.829a.5.5 0 0 0 0 1h1.829ZM13.293 10A.5.5 0 1 0 14 9.293L12.707 8a.5.5 0 1 0-.707.707L13.293 10ZM9.5 11.157a.5.5 0 0 0 1 0V9.328a.5.5 0 0 0-1 0v1.829Zm1.854-5.097a.5.5 0 0 0 0-.706l-.708-.708a.5.5 0 0 0-.707 0L8.646 5.94a.5.5 0 0 0 0 .707l.708.708a.5.5 0 0 0 .707 0l1.293-1.293Zm-3 3a.5.5 0 0 0 0-.706l-.708-.708a.5.5 0 0 0-.707 0L.646 13.94a.5.5 0 0 0 0 .707l.708.708a.5.5 0 0 0 .707 0L8.354 9.06Z"/>
                  </svg>
                </button>

                <!-- Main Search Button -->
                <!-- En FUTUR/FWORKSPACE le bouton reste actif quand un workspace est sélectionné
                     (pas de bouton Agent séparé pour ces modes — le dispatch se fait via selectedQueryMode='AGENT'
                     forcé par le watcher selectedWorkspaceId). -->
                <button
                  class="btn button-base search-button tech-purple"
                  style="font-size: 12px; margin-left: 10px;"
                  @click="handleSearchSubmit"
                  :disabled="isSubmittingSearch || isSubmittingFlash || isSubmittingAgent || isSubmittingTableau || selectedSourcesCount === 0 || (selectedWorkspaceId !== null && !isFuturUser)"
                  :title="(selectedWorkspaceId !== null && !isFuturUser) ? 'Workspace sélectionné — utilisez le bouton Agent' : ''"
                >
                  {{ (isSubmittingSearch || isSubmittingFlash || isSubmittingAgent || isSubmittingTableau) ? $t('dashboard.common.sending') : mainButtonText }}
                </button>
              </div>
              <p v-if="selectedSourcesCount === 0" class="source-select-message">
                {{ $t('validation.sourceRequired') }}
              </p>
            </div>
          </div>

          <div v-if="isLoadingDossiers" class="spinner-container my-4">
            <div class="spinner"></div>
          </div>

          <!-- FINDER LAYOUT -->
          <div v-if="!isSearchActive && allItems.length > 1" class="finder-container mt-4">
            <!-- LEFT SIDEBAR: Groups -->
            <div class="finder-sidebar">
              <div class="sidebar-header">
                <h6>{{ $t('dashboard.dossier.sidebarTitle') }}</h6>
                <button class="btn-add-group" @click="openCreateGroupModal" :title="$t('dashboard.dossier.newDossier')">
                  +
                </button>
              </div>

              <div class="sidebar-list">
                <!-- All Searches -->
                <div
                  class="sidebar-item"
                  :class="{ active: selectedGroupId === null }"
                  @click="selectGroup(null)"
                >

                  <svg class="sidebar-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M1 2.5A1.5 1.5 0 0 1 2.5 1h3A1.5 1.5 0 0 1 7 2.5v3A1.5 1.5 0 0 1 5.5 7h-3A1.5 1.5 0 0 1 1 5.5zm8 0A1.5 1.5 0 0 1 10.5 1h3A1.5 1.5 0 0 1 15 2.5v3A1.5 1.5 0 0 1 13.5 7h-3A1.5 1.5 0 0 1 9 5.5zm-8 8A1.5 1.5 0 0 1 2.5 9h3A1.5 1.5 0 0 1 7 10.5v3A1.5 1.5 0 0 1 5.5 15h-3A1.5 1.5 0 0 1 1 13.5zm8 0A1.5 1.5 0 0 1 10.5 9h3a1.5 1.5 0 0 1 1.5 1.5v3a1.5 1.5 0 0 1-1.5 1.5h-3A1.5 1.5 0 0 1 9 13.5z"/>
                  </svg>
                  <span>{{ $t('dashboard.dossier.allSearches') }}</span>
                </div>

                <!-- Groups -->
                <div
                  v-for="group in groups"
                  :key="group.id"
                  class="sidebar-item"
                  :class="{ active: selectedGroupId === group.id }"
                  @click="selectGroup(group.id)"
                >
                  <svg class="sidebar-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M9.828 3h3.982a2 2 0 0 1 1.992 2.181l-.637 7A2 2 0 0 1 13.174 14H2.825a2 2 0 0 1-1.991-1.819l-.637-7a2 2 0 0 1 .342-1.31L.5 3a2 2 0 0 1 2-2h3.672a2 2 0 0 1 1.414.586l.828.828A2 2 0 0 0 9.828 3m-8.322.12q.322-.119.684-.12h5.396l-.707-.707A1 1 0 0 0 6.172 2H2.5a1 1 0 0 0-1 .981z"/>
                  </svg>
                  <span>{{ group.name }}</span>
                  <img
                    src="@/assets/card-more.svg"
                    :alt="$t('dashboard.dossier.moreOptions')"
                    class="sidebar-more-icon"
                    @click.stop="toggleGroupMenu(group.id)"
                  >
                  <div v-if="openGroupMenuId === group.id" class="dossier-menu">
                    <button class="menu-item" @click.stop="startRename({...group, type: 'group'})">{{ $t('common.rename') }}</button>
                    <button class="delete-button" @click.stop="startDeleteGroup({...group, dossiers: getGroupDossiers(group.id)})">{{ $t('common.delete') }}</button>
                  </div>
                </div>
              </div>
            </div>

            <!-- RIGHT PANEL: Dossiers/Searches -->
            <div class="finder-main">
              <div class="finder-header">
                <div class="dossier-search-container">
                  <svg class="search-icon-grey" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0"/>
                  </svg>
                  <input
                    type="text"
                    v-model="dossierSearchQuery"
                    :placeholder="$t('dashboard.dossier.searchPlaceholder')"
                    class="dossier-search-input"
                    @focus="onSearchFocus"
                    :disabled="isLoadingAllDossiers"
                  />
                  <div v-if="isLoadingAllDossiers" class="search-loading-indicator">
                    <div class="mini-spinner"></div>
                  </div>
                </div>
              </div>

              <div v-if="dossierSearchQuery && !filteredDisplayDossiers.length" class="text-center my-5 no-results-message">
                <p>{{ $t('dashboard.dossier.noResults') }}</p>
              </div>

              <div class="finder-grid">
                <button
                  v-for="dossier in displayDossiers"
                  :key="dossier.id"
                  type="button"
                  class="finder-card"
                  :class="{ 'pinned-card': dossier.is_pinned }"
                  @click="openDossier(dossier, 0)"
                >
                  <div class="finder-card-header">
                    <!-- Partie gauche (épingle + date) -->
                    <div class="finder-card-header-left">
                      <svg
                        @click.stop="togglePinDossier(dossier)"
                        class="pin-icon"
                        :class="{ 'pinned': dossier.is_pinned }"
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        :title="dossier.is_pinned ? $t('dashboard.dossier.unpin') : $t('dashboard.dossier.pin')"
                      >
                        <path d="M12 17v5"/>
                        <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z"/>
                      </svg>

                      <span class="dossier-date">
                        {{ formatDossierDate(dossier.updated_at) }}
                      </span>

                      <span
                        class="dossier-mode-tag"
                        :class="`dossier-mode-tag--${(dossier.MODE || 'LONG').toLowerCase()}`"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="10"
                          height="10"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                          class="dossier-mode-tag-icon"
                        >
                          <path :d="dossierModeIconPath(dossier.MODE)"/>
                        </svg>
                        {{ dossierModeLabel(dossier.MODE) }}
                      </span>
                    </div>

                    <!-- Partie droite (info + menu) -->
                    <div class="finder-card-header-right">
                      <!-- NOUVEAU: Icône d'information -->
                      <svg
                        @click.stop="openInfoModal(dossier)"
                        class="info-icon"
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        fill="#6c63ff"
                        viewBox="0 0 16 16"
                        :title="$t('dashboard.dossier.viewFullQuestion')"
                      >
                        <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16m.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2"/>
                      </svg>

                      <!-- Menu existant -->
                      <img
                        src="@/assets/card-more.svg"
                        :alt="$t('dashboard.dossier.moreOptions')"
                        class="more-icon"
                        @click.stop="toggleDossierMenu(dossier.id)"
                      >
                    </div>

                    <!-- Menus existants (ne pas toucher) -->
                    <div v-if="openMenuId === dossier.id" class="dossier-menu">
                      <button class="menu-item" @click.stop="togglePinDossier(dossier)">
                        {{ dossier.is_pinned ? $t('dashboard.dossier.unpin') : $t('dashboard.dossier.pin') }}
                      </button>
                      <button class="menu-item" v-if="dossier.group_id" @click.stop="assignDossierToGroup(dossier, null)">{{ $t('dashboard.dossier.removeFromGroup') }}</button>
                      <button class="menu-item" @click.stop="openGroupAssignmentMenu(dossier)">{{ $t('dashboard.dossier.moveTo') }}</button>
                      <button class="menu-item" @click.stop="startRename({...dossier, type: 'dossier'})">{{ $t('common.rename') }}</button>
                      <button class="delete-button" @click.stop="deleteDossier(dossier)">{{ $t('common.delete') }}</button>
                    </div>

                    <div v-if="showGroupAssignmentMenu === dossier.id" class="dossier-menu group-assignment-menu">
                      <button v-for="g in groups" :key="g.id" @click.stop="assignDossierToGroup(dossier, g.id)">{{ g.name }}</button>
                      <hr v-if="groups.length > 0 && dossier.group_id">
                      <button class="text-muted" v-if="dossier.group_id" @click.stop="assignDossierToGroup(dossier, null)">{{ $t('dashboard.dossier.removeFromGroupParens') }}</button>
                    </div>
                  </div>

                  <div class="finder-card-body">
                    {{ dossier.name || $t('dashboard.dossier.newDossier') }}
                  </div>
                </button>
              </div>

              <!-- Load More Button -->
              <div v-if="hasMoreDossiers && !dossierSearchQuery && selectedGroupId === null" class="text-center mt-3">
                <button
                  @click="loadMoreDossiers"
                  class="btn button-base outline-button"
                  :disabled="loadingMoreDossiers"
                >
                  <div v-if="loadingMoreDossiers" class="mini-spinner mr-2"></div>
                  {{ loadingMoreDossiers ? $t('common.loading') : $t('dashboard.dossier.loadMore') }}
                </button>
              </div>
            </div>
          </div>

          <div v-else-if="isSearchActive" class="synthese-container mt-4">
            <div class="synthese-content">
              <div v-if="isLoadingPoints" class="loading-message">
                <div class="title-points">
                  <h4 style="font-size: 1.2rem; font-weight: 300">
                    <em>{{ $t('dashboard.points.loading') }}</em>
                  </h4>
                  <div class="spinner ml-3"></div>
                </div>
              </div>

              <div v-else-if="sourceUnavailable" class="source-unavailable-message">
                <p>{{ $t('dashboard.points.sourceUnavailable') }}</p>
                <button @click="cancelSearch" class="back-button">{{ $t('common.back') }}</button>
              </div>

              <div v-else-if="pointsList.length === 0">
                <div class="buttons">
                  <button @click="fetchPoints" class="enrichir-button">
                    {{ $t('dashboard.points.launchSearch') }}
                  </button>
                  <button @click="cancelSearch" class="back-button">{{ $t('common.cancel') }}</button>
                </div>
              </div>

              <transition name="fade">
                <div v-if="pointsList.length !== 0" class="points-class">
                  <div class="title-points">
                    <h4 style="font-size: 1.2rem; font-weight: 300">
                      <em>
                        {{ $t('dashboard.points.systemWillSearch') }}
                      </em>
                    </h4>
                  </div>
                  <ul class="points-list">
                    <li
                      v-for="(point, index) in pointsList"
                      :key="point.id"
                      class="point-item"
                    >
                      <div v-if="point.isEditing" class="point-display">
                        <textarea
                          ref="editTextArea"
                          v-model="point.text"
                          rows="2"
                          class="point-text bg-light"
                          :style="{ height: point.editingHeight }"
                          style="resize: none;"
                        ></textarea>
                        <button @click="savePoint(index)" class="small-button">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            class="bi bi-check-circle"
                            viewBox="0 0 16 16"
                          >
                            <path
                              d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"
                            />
                            <path
                              d="m10.97 4.97-.02.022-3.473 4.425-2.093-2.094a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-1.071-1.05"
                            />
                          </svg>
                        </button>
                      </div>
                      <div v-else class="point-display">
                        <div ref="pointText" class="point-text bg-light">
                          <div style="margin-right: 1em; width: 74%;">
                            {{ point.text }}
                          </div>
                          <button @click="editPoint(index)" class="small-button icon-button">
                            <img src="@/assets/button-modify-point.svg" :alt="$t('common.edit')">
                            <span class="icon-label">{{ $t('common.edit') }}</span>
                          </button>
                          <button @click="deletePoint(index)" class="small-button icon-button">
                            <img src="@/assets/button-delete-point.svg" :alt="$t('common.delete')">
                            <span class="icon-label-red">{{ $t('common.delete') }}</span>
                          </button>
                        </div>
                      </div>
                    </li>
                  </ul>

                  <!-- Chat interface for refinement -->
                  <div class="refinement-chat-container">
                    <div class="chat-header">
                      <h5>{{ $t('dashboard.refinement.title') }}</h5>
                      <p class="chat-subtitle">{{ $t('dashboard.refinement.subtitle') }}</p>
                    </div>

                    <!-- Loading state during refinement -->
                    <div v-if="isRefining" class="refinement-loading">
                      <div class="spinner ml-3"></div>
                      <em>{{ $t('dashboard.points.loading') }}</em>
                    </div>

                    <!-- Toggle button to show/hide refinement input -->
                    <div v-if="!showRefinementInput && !isRefining" class="refinement-toggle-container">
                      <button @click="showRefinementInput = true" class="btn refinement-toggle-button">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style="margin-right: 8px;">
                          <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z"/>
                        </svg>
                        {{ $t('dashboard.refinement.modifyButton') }}
                      </button>
                    </div>

                    <!-- Dashboard-style refinement input (only shown when toggled) -->
                    <div v-if="showRefinementInput && !isRefining" class="refinement-input-section">
                      <div class="input-container">
                        <textarea
                          v-model="refinementInput"
                          class="form-control search-input"
                          :placeholder="$t('dashboard.refinement.inputPlaceholder')"
                          rows="1"
                          ref="refinementTextarea"
                          @input="adjustRefinementHeight"
                          @keydown.enter.exact.prevent="sendRefinementMessage"
                        ></textarea>
                      </div>
                      <div class="buttons-line" style="margin-top: 10px;">
                        <button
                          class="btn button-base search-button tech-purple"
                          style="font-size: 12px;"
                          @click="sendRefinementMessage"
                          :disabled="!refinementInput.trim()"
                        >
                          {{ $t('common.send') }}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div class="buttons">
                    <button @click="cancelSearch" class="back-button">
                      {{ $t('common.cancel') }}
                    </button>
                    <button
                      @click="startAnalysis"
                      class="enrichir-button"
                      :disabled="pointsList.length === 0"
                      :class="{ 'button-disabled': pointsList.length === 0 }"
                    >
                      {{ $t('dashboard.points.startSearch') }}
                    </button>
                  </div>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DeleteConfirmationModal from './DeleteConfirmationModal.vue';
import DeleteGroupModal from './DeleteGroupModal.vue';
import SubscriptionModal from './SubscriptionModal.vue';
import RenameDossierModal from './RenameDossierModal.vue';
import CreateGroupModal from './CreateGroupModal.vue';
import WorkspaceCreateModal from './WorkspaceCreateModal.vue';
import WorkspaceManageModal from './WorkspaceManageModal.vue';
import WorkspaceReorganizeModal from './WorkspaceReorganizeModal.vue';
import TemplateManagerModal from './redacteur/TemplateManagerModal.vue';
import { supabase } from "@/supabase/supabaseClient";
import axios from "axios";
import { getSubscription } from "@/services/api";
import { getSupabaseToken } from "@/services/api";
import { getCurrentUser } from "@/supabase/auth";
import { safeRangeQuery } from "@/services/supabaseRange";
import { detectCountry, getSources } from "@/services/countries";

const API_URL = process.env.VUE_APP_API_URL || 'https://api.query-juriste.com'

export default {
  name: 'DashboardPage',

  components: {
    DeleteConfirmationModal,
    DeleteGroupModal,
    SubscriptionModal,
    RenameDossierModal,
    CreateGroupModal,
    WorkspaceCreateModal,
    WorkspaceManageModal,
    WorkspaceReorganizeModal,
    TemplateManagerModal,
  },

  data() {
    return {
      // General State
      isMaintenanceMode: false,
      lastCreatedAt: null,
      pinnedDossiers: [],
      currentUserId: null,
      isSubmittingFlash: false,
      isSubmittingAgent: false,
      isSubmittingTableau: false,
      isSubmittingSearch: false,
      showTableauModal: false,
      tableauSubQuestions: [],
      tableauSubQuestionsLoading: false,
      tableauSubQuestionsMax: 10,

      // Improve question state (baguette magique)
      showImproveModal: false,
      improveStep: 'loading', // 'loading' | 'questions' | 'rewriting' | 'result'
      improveQuestions: [],
      improveAnswers: [],
      improvedQuestion: '',
      improveOriginalQuestion: '',
      userMode: null,
      userModeLoaded: false,
      showModeDropdown: false,
      selectedQueryMode: null, // null = Réponse longue, 'FLASH' = rapide, 'TABLEAU' = tableau

      // Dossier & Group State
      dossiers: [],
      groups: [],
      isLoadingDossiers: true,
      expandedGroupId: null,
      showAllDossiers: false,
      dossierSearchQuery: '',

      // NOUVEAU: Pour le mode Finder
      selectedGroupId: null,

      // Pagination State
      dossiersPage: 0,
      dossiersPerPage: 10,
      hasMoreDossiers: false,
      loadingMoreDossiers: false,
      loadingGroupDossiers: false,

      allDossiersLoaded: false,
      isLoadingAllDossiers: false,

      // Menu & Modal State
      openMenuId: null,
      openGroupMenuId: null,
      showGroupAssignmentMenu: null,
      dossierToDelete: null,
      groupToDelete: null,
      showDeleteModal: false,
      itemToRename: null,
      showRenameModal: false,
      showCreateGroupModal: false,
      showInfoModal: false,
      selectedDossierInfo: '',

      // Workspaces (pour le mode Agent — tool query_workspace)
      workspaces: [],
      selectedWorkspaceId: null,
      showWorkspaceDropdown: false,
      showCreateWorkspaceModal: false,
      showManageWorkspaceModal: false,
      manageWorkspaceInitialId: null,
      reorgWorkspace: null,
      // Périmètre de recherche : liste de workspace_documents.id (null = tout)
      selectedWorkspaceScope: null,
      workspacePollTimer: null,
      // Ouverture auto du dropdown après fermeture du modal de création
      openWorkspaceDropdownOnClose: false,

      // Search State
      searchQuery: '',
      isSearchActive: false,
      pointsList: [],
      global_contexte: '',
      isLoadingPoints: false,
      pointIdCounter: 0,
      sourceUnavailable: false, // Message affiché quand l'IA n'a pas accès aux sources

      // Refinement Chat State
      chatMessages: [],
      refinementInput: '',
      isRefining: false,
      showRefinementInput: false, // Toggle for showing the refinement textbox

      // Subscription State
      sub: { plan: "none", status: "none", queries_used: 0, current_period_end: null },
      showPaywall: false,
      showQuotaExceededModal: false,

      // Speech-to-text state
      isRecording: false,
      mediaRecorder: null,
      audioChunks: [],
      isTranscribing: false,

      // Sources selection state
      showSourcesDropdown: false,
      country: detectCountry(),
      availableSources: getSources().map(s => ({ ...s })),

      // Bouton « + » (popover Sources / Rédiger) + Rédacteur
      showPlusMenu: false,
      showTemplateManager: false,
      // Chip violet dans la barre de recherche : { id, name, skillId }
      selectedTemplateChip: null,
      // Rédacteur réservé au mode billing FULL (et au produit FR : UI non traduite)
      isFullUser: false,
    };
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
    document.removeEventListener('keydown', this.handleGlobalKeydown);
    window.removeEventListener('popstate', this.handlePopState);
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    if (this.unsubscribe) {
      this.unsubscribe();
    }
    this.stopWorkspacePolling();
  },

  watch: {
    dossierSearchQuery: {
      // eslint-disable-next-line no-unused-vars
      handler(newQuery, oldQuery) {
        if (this.searchTimeout) {
          clearTimeout(this.searchTimeout);
        }

        if (newQuery.trim() && !this.allDossiersLoaded) {
          this.searchTimeout = setTimeout(() => {
            this.loadAllDossiersWithFocus();
          }, 300);
        }
      },
      immediate: false
    },
    // Sélectionner un workspace n'a de sens qu'en mode AGENT. Pour FUTUR/FWORKSPACE
    // (où le bouton "Agent" séparé n'existe pas), on force le mode AGENT pour que
    // le bouton Recherche dispatche vers handleAgentQuery via handleSearchSubmit.
    selectedWorkspaceId(newId) {
      if (newId !== null && this.isFuturUser) {
        this.selectedQueryMode = 'AGENT';
      }
    }
  },

  computed: {
    isAgentUser() {
      // Les modes TABLEAU, WORKSPACE, FUTUR et FWORKSPACE partagent les features agent
      // (WORKSPACE = TABLEAU + workspace button, FUTUR = Agent intégré dans le sélecteur de mode,
      //  FWORKSPACE = FUTUR + workspace button)
      return ['TABLEAU', 'WORKSPACE', 'FUTUR', 'FWORKSPACE'].includes(this.userMode);
    },
    isWorkspaceUser() {
      return this.userMode === 'WORKSPACE' || this.userMode === 'FWORKSPACE';
    },
    canUseRedacteur() {
      // Mode billing FULL uniquement, et produit FR seulement (UI non traduite).
      // Le backend impose la même règle (403) : ceci ne fait que cacher l'entrée.
      return this.isFullUser && this.country === 'FR';
    },
    isFuturUser() {
      return this.userMode === 'FUTUR' || this.userMode === 'FWORKSPACE';
    },
    dossiersByGroup() {
      const groupMap = this.groups.reduce((acc, group) => {
        acc[group.id] = { ...group, type: 'group', dossiers: [] };
        return acc;
      }, {});

      const ungrouped = [];

      this.dossiers.forEach(dossier => {
        if (dossier.group_id && groupMap[dossier.group_id]) {
          groupMap[dossier.group_id].dossiers.push(dossier);
        } else {
          ungrouped.push(dossier);
        }
      });

      const result = Object.values(groupMap);

      if (ungrouped.length > 0) {
        result.push({ id: 'ungrouped', name: this.$t('dashboard.dossier.ungroupedSearches'), dossiers: ungrouped });
      }

      return result;
    },

    displayDossiers() {
      const filtered = this.filteredDisplayDossiers;

      // Séparer les dossiers épinglés et non épinglés
      const pinned = filtered.filter(d => d.is_pinned);
      const unpinned = filtered.filter(d => !d.is_pinned);

      // Trier les dossiers épinglés par date de mise à jour (les plus récents en premier)
      pinned.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));

      // Les dossiers épinglés apparaissent en premier
      return [...pinned, ...unpinned];
    },

    allItems() {
       const createCard = {
        type: 'create-group',
        id: 'create-new-dossier-card',
        name: this.$t('dashboard.dossier.newDossierTitle')
      };

      const groups = this.dossiersByGroup
        .filter(g => g.id !== 'ungrouped')
        .map(g => ({
          type: 'group',
          id: g.id,
          name: g.name,
          dossiers: g.dossiers
        }));

      const ungroupedDossiers = this.dossiersByGroup
        .find(g => g.id === 'ungrouped')?.dossiers || [];

      const formattedUngrouped = ungroupedDossiers.map(d => ({
        type: 'dossier',
        ...d
      }));

      return [createCard, ...groups, ...formattedUngrouped];
    },

    filteredItems() {
      const trimmedQuery = this.dossierSearchQuery.trim().toLowerCase();

      if (!trimmedQuery) {
        return this.allItems;
      }

      const keywords = trimmedQuery.split(' ').filter(kw => kw.length > 0);

      if (keywords.length === 0) {
        return this.allItems;
      }

      const textMatchesAllKeywords = (text) => {
        if (!text) return false;
        const lowerText = text.toLowerCase();
        return keywords.every(kw => lowerText.includes(kw));
      };

      return this.allItems.filter(item => {
        if (item.type === 'create-group') {
          return false;
        }

        if (textMatchesAllKeywords(item.name)) {
          return true;
        }

        if (item.type === 'dossier' && (textMatchesAllKeywords(item.contexte) || textMatchesAllKeywords(item.synthese))) {
          return true;
        }

        if (item.type === 'group') {
          return item.dossiers.some(dossier =>
            textMatchesAllKeywords(dossier.name) || textMatchesAllKeywords(dossier.contexte) || textMatchesAllKeywords(dossier.synthese)
          );
        }

        return false;
      });
    },

    expandedGroup() {
      if (!this.expandedGroupId) {
        return null;
      }
      return this.dossiersByGroup.find(g => g.id === this.expandedGroupId);
    },

    quotaRemaining() {
      if (this.sub.plan === "student") return 25 - this.sub.queries_used;
      if (this.sub.plan === "premium") return 100;
      return 100;
    },

    formattedCurrentPeriodEnd() {
      return this.sub.current_period_end
        ? this.$localeDate(new Date(this.sub.current_period_end), {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })
        : '';
    },

    canRequest() {
      if (this.sub.plan === "lifetime_free") return true;
      const active = this.sub.status === "active" || this.sub.status === "trialing";
      const paying = ["student", "premium", "trialing"].includes(this.sub.plan);

      let periodValid = true;
      if (this.sub.current_period_end) {
        const periodEnd = new Date(this.sub.current_period_end);
        const now = new Date();
        periodValid = periodEnd > now;
      }

      const underQueryLimit = this.sub.queries_used < 100000;

      return active && paying && periodValid && this.quotaRemaining > 0 && underQueryLimit;
    },

    trialDaysRemaining() {
      if (this.sub.status !== 'trialing' || !this.sub.current_period_end) return 0;
      const now = new Date();
      const end = new Date(this.sub.current_period_end);
      const diffTime = end - now;
      return diffTime > 0 ? Math.ceil(diffTime / (1000 * 60 * 60 * 24)) : 0;
    },

    isTrialing() {
      return this.sub.status === 'trialing';
    },

    // NOUVELLES computed properties pour le mode Finder
    ungroupedDossiers() {
      return this.dossiers.filter(d => !d.group_id);
    },

    selectedGroupDossiers() {
      if (this.selectedGroupId === null) {
        return this.dossiers;
      } else if (this.selectedGroupId === 'ungrouped') {
        return this.ungroupedDossiers;
      } else {
        return this.dossiers.filter(d => d.group_id === this.selectedGroupId);
      }
    },

    filteredDisplayDossiers() {
      const trimmedQuery = this.dossierSearchQuery.trim().toLowerCase();

      if (!trimmedQuery) {
        return this.selectedGroupDossiers;
      }

      const keywords = trimmedQuery.split(' ').filter(kw => kw.length > 0);

      return this.selectedGroupDossiers.filter(dossier => {
        const text = `${dossier.name || ''} ${dossier.contexte || ''} ${dossier.synthese || ''}`.toLowerCase();
        return keywords.every(kw => text.includes(kw));
      });
    },

    mainButtonText() {
      if (this.isAgentUser) {
        return this.$t('common.search');
      }
      const flashButtonVisible = this.userModeLoaded && (!this.isTrialing || this.userMode === 'FLASH');
      return flashButtonVisible ? this.$t('dashboard.modes.longAnswer') : this.$t('common.search');
    },

    // Sources computed properties
    principalSources() {
      return this.availableSources.filter(s => s.category === 'principal');
    },

    secondarySources() {
      return this.availableSources.filter(s => s.category === 'secondary');
    },

    selectedSourcesCount() {
      return this.availableSources.filter(s => s.selected).length;
    },

    selectedSourceIds() {
      return this.availableSources.filter(s => s.selected).map(s => s.id);
    },
  },

  async mounted() {
    document.addEventListener('click', this.handleClickOutside);
    document.addEventListener('keydown', this.handleGlobalKeydown);
    window.addEventListener('popstate', this.handlePopState);


    // 1. D'abord charger la subscription
    await this.refreshSubscription();
    try {
      const user = await getCurrentUser();
      if (user) {
        this.currentUserId = user.id;
        await this.fetchUserMode(); // 👈 NOUVEAU
      }
    } catch (error) {
      console.error('Error getting current user:', error);
    }

    // 2. PUIS gérer la pending query
    const pendingQuery = localStorage.getItem('pendingSearchQuery');
    const pendingSearchMode = localStorage.getItem('pendingSearchMode');
    const pendingWorkspaceId = localStorage.getItem('pendingWorkspaceId');
    if (pendingQuery) {
      this.searchQuery = pendingQuery;
      if (pendingWorkspaceId) {
        this.selectedWorkspaceId = pendingWorkspaceId;
      }
      localStorage.removeItem('pendingSearchQuery');
      localStorage.removeItem('pendingSearchMode');
      localStorage.removeItem('pendingWorkspaceId');

      this.$nextTick(() => {
        if (this.$refs.searchBox) {
          this.adjustTextareaHeight();

          // Scroller tout en bas de la textarea
          const searchBox = this.$refs.searchBox;
          searchBox.scrollTop = searchBox.scrollHeight;

          // Positionner le curseur à la fin
          const questionIndex = pendingQuery.indexOf("Question:");
          if (questionIndex > 0) {
            searchBox.setSelectionRange(questionIndex, questionIndex);
          }
        }

        // 3. Lancer la recherche auto dans le MÊME mode que le dossier d'origine
        //    (sinon un user agent retomberait toujours en mode agent par défaut)
        if (pendingSearchMode === 'FLASH') {
          this.selectedQueryMode = 'FLASH';
          this.handleFlashQuery();
        } else if (pendingSearchMode === 'AGENT') {
          this.selectedQueryMode = 'AGENT';
          this.handleAgentQuery();
        } else if (pendingSearchMode === 'TABLEAU') {
          this.selectedQueryMode = 'TABLEAU';
          this.openTableauModal();
        } else {
          // STANDARD / réponse longue : forcer le mode long, ne pas dispatcher vers l'agent
          this.selectedQueryMode = null;
          this.handleSearchSubmit();
        }
      });
    }

    // 4. Ensuite charger les dossiers
    this.fetchDossiersAndGroups();
    this.fetchWorkspaces();

    this.$nextTick(() => {
      if (this.$refs.searchBox) {
        this.adjustTextareaHeight();
      }
    });

    this.refreshSubscription();
    try {
      const user = await getCurrentUser();
      if (user) {
        this.currentUserId = user.id;
      }
    } catch (error) {
      console.error('Error getting current user:', error);
    }
  },

  methods: {
    // NOUVELLE méthode pour le mode Finder
    selectGroup(groupId) {
      this.selectedGroupId = groupId;
      this.dossierSearchQuery = '';

      if (groupId && groupId !== 'ungrouped') {
        this.loadAllGroupDossiers(groupId);
      }
    },

    // ── Workspaces (mode Agent : tool query_workspace) ──
    async fetchWorkspaces() {
      try {
        const user = await getCurrentUser();
        if (!user) return;
        const { data, error } = await supabase
          .from('workspaces')
          .select('id, name, status, ingest_error, reorg_source_id')
          .eq('user_id', user.id)
          .order('updated_at', { ascending: false });
        if (error) throw error;
        this.workspaces = data || [];
        this.ensureWorkspacePolling();
      } catch (e) {
        console.error('fetchWorkspaces failed', e);
      }
    },
    ensureWorkspacePolling() {
      // Active le polling tant qu'au moins un workspace est dans un état transitoire
      // (ni 'ready' ni 'failed'). Arrête dès que tout est terminal.
      const hasPending = this.workspaces.some((w) => w.status !== 'ready' && w.status !== 'failed');
      if (hasPending && !this.workspacePollTimer) {
        this.workspacePollTimer = setInterval(() => this.fetchWorkspaces(), 5000);
      } else if (!hasPending && this.workspacePollTimer) {
        clearInterval(this.workspacePollTimer);
        this.workspacePollTimer = null;
      }
    },
    stopWorkspacePolling() {
      if (this.workspacePollTimer) {
        clearInterval(this.workspacePollTimer);
        this.workspacePollTimer = null;
      }
    },
    toggleWorkspaceDropdown() {
      this.showWorkspaceDropdown = !this.showWorkspaceDropdown;
      if (this.showWorkspaceDropdown) {
        // Un seul dropdown ouvert à la fois — évite la superposition
        this.showSourcesDropdown = false;
        this.showModeDropdown = false;
        this.showPlusMenu = false;
        // Rafraîchit la liste à l'ouverture pour un statut à jour
        this.fetchWorkspaces();
      }
    },
    selectWorkspace(wsOrId) {
      // Toute sélection directe repart sur le workspace ENTIER — le périmètre
      // ne se pose que via le modal Gérer (onWorkspaceSelectedFromModal).
      this.selectedWorkspaceScope = null;
      // Accepte soit un id (ancien signature : null ou string), soit un objet workspace
      if (wsOrId === null) {
        this.selectedWorkspaceId = null;
      } else if (typeof wsOrId === 'string') {
        this.selectedWorkspaceId = wsOrId;
      } else {
        // Objet workspace : vérifier qu'il est prêt
        if (wsOrId.status !== 'ready') return;
        this.selectedWorkspaceId = wsOrId.id;
      }
      this.showWorkspaceDropdown = false;
    },
    openCreateWorkspaceModal() {
      this.showCreateWorkspaceModal = true;
      this.showWorkspaceDropdown = false;
    },
    onWorkspaceSelectedFromModal(ws) {
      this.showManageWorkspaceModal = false;
      this.manageWorkspaceInitialId = null;
      this.selectWorkspace(ws);
      // Après selectWorkspace (qui remet le périmètre à null) : poser le
      // périmètre choisi dans l'arbre du modal, s'il y en a un.
      if (this.selectedWorkspaceId === ws.id && ws.scope && ws.scope.length) {
        this.selectedWorkspaceScope = [...ws.scope];
      }
    },
    openReorganizeModal(ws) {
      this.reorgWorkspace = ws;
      this.showWorkspaceDropdown = false;
    },
    onReorganizeCreated(workspace) {
      this.reorgWorkspace = null;
      this.onWorkspaceCreated(workspace);
      if (this.$toast) this.$toast.success('Réorganisation lancée — un nouveau workspace a été créé.');
    },
    openManageWorkspaceModal(wsId) {
      // Appelé soit depuis le footer du dropdown (sans id — l'arg est alors le
      // MouseEvent), soit depuis l'action « Gérer » d'une ligne (avec l'id).
      this.manageWorkspaceInitialId = typeof wsId === 'string' ? wsId : null;
      this.showManageWorkspaceModal = true;
      this.showWorkspaceDropdown = false;
    },
    async downloadWorkspaceArchive(ws) {
      try {
        const token = await getSupabaseToken();
        const resp = await fetch(`${API_URL}/copilot/api/v1/workspace/${ws.id}/archive-url`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: '{}',
        });
        if (!resp.ok) throw new Error(`archive-url ${resp.status}`);
        const data = await resp.json();
        if (data.url) window.open(data.url, '_blank');
      } catch (e) {
        console.error('workspace archive download failed', e);
        if (this.$toast) this.$toast.error("L'archive n'est pas disponible pour ce workspace.");
      }
    },
    onWorkspaceCreated(workspace) {
      // Ajout optimiste : le workspace apparaît immédiatement dans la liste avec son statut initial
      const exists = this.workspaces.some((w) => w.id === workspace.id);
      if (!exists) {
        this.workspaces.unshift({
          id: workspace.id,
          name: workspace.name,
          status: workspace.status || 'draft',
          ingest_error: null,
        });
      }
      this.ensureWorkspacePolling();
    },
    onIngestStarted({ id }) {
      // Mise à jour optimiste du statut vers 'ingesting'
      const ws = this.workspaces.find((w) => w.id === id);
      if (ws) ws.status = 'ingesting';
      this.openWorkspaceDropdownOnClose = true;
      this.ensureWorkspacePolling();
    },
    onCreateWorkspaceClosed() {
      this.showCreateWorkspaceModal = false;
      if (this.openWorkspaceDropdownOnClose) {
        this.openWorkspaceDropdownOnClose = false;
        // setTimeout(0) : le clic sur "Fermer" remonte au document et déclenche
        // handleClickOutside, qui ferme tout dropdown dont la cible n'est pas
        // dans .workspace-selector-container. On ouvre donc APRÈS la propagation.
        setTimeout(() => {
          this.showWorkspaceDropdown = true;
          this.fetchWorkspaces();
        }, 0);
      }
    },
    onWorkspaceDeleted({ id }) {
      this.workspaces = this.workspaces.filter((w) => w.id !== id);
      if (this.selectedWorkspaceId === id) {
        this.selectedWorkspaceId = null;
      }
      this.ensureWorkspacePolling();
    },
    selectedWorkspaceName() {
      const w = this.workspaces.find((x) => x.id === this.selectedWorkspaceId);
      return w ? w.name : '';
    },

    async fetchUserMode() {
      try {
        const user = await getCurrentUser();
        if (user) {
          const { data, error } = await supabase
            .schema('billing')  // ✅ Spécifier le schéma séparément
            .from('customers')   // ✅ Puis la table
            .select('mode')
            .eq('id', user.id)
            .single();

          if (error) throw error;

          const rawMode = data?.mode || null;
          // Interface unifiée : tout le monde en FUTUR ; seul un accès workspace existant → FWORKSPACE.
          // Les modes VICTIME/BOOSTED n'existent plus : le mode ne pilote que workspace/Rédacteur.
          // FULL = FWORKSPACE (workspace inclus) + accès au Rédacteur.
          const hasWorkspace = rawMode === 'WORKSPACE' || rawMode === 'FWORKSPACE' || rawMode === 'FULL';
          this.isFullUser = rawMode === 'FULL';
          this.userMode = hasWorkspace ? 'FWORKSPACE' : 'FUTUR';

          // FUTUR / FWORKSPACE → sélecteur visible, mode "Agent" par défaut
          this.selectedQueryMode = 'AGENT';
        }
      } catch (error) {
        console.error('Error fetching user mode:', error);
        this.userMode = 'FUTUR';
      } finally {
        this.userModeLoaded = true;  // ✅ AJOUTER CETTE LIGNE
      }
    },



    async handleFlashQuery() {
    if (this.isSubmittingFlash || this.isSubmittingAgent || this.isSubmittingSearch || this.isSubmittingTableau) {
      return;
    }

    // Chip Rédacteur actif : le chip implique le mode agent, on délègue au
    // flux agent (qui porte le template_id).
    if (this.selectedTemplateChip) {
      this.handleAgentQuery();
      return;
    }

    if (!this.searchQuery.trim()) {
      alert(this.$t('validation.searchRequired'));
      return;
    }

    // Check if at least one source is selected
    if (this.selectedSourcesCount === 0) {
      this.$toast.warning(this.$t('validation.sourceRequired'));
      return;
    }

    if (!(await this.ensureSubscriptionAllows())) return;

    this.isSubmittingFlash = true;

    // Détection 1ère recherche (historique local vide AVANT l'insert du nouveau dossier).
    // Backend re-vérifie via COUNT — ici on envoie juste le hint.
    const isFirstSearch = (this.dossiers?.length || 0) === 0;

    try {
      const user = await getCurrentUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Create dossier
      const dossierName = this.searchQuery.length > 30
        ? this.searchQuery.substring(0, 30) + '...'
        : this.searchQuery;

      const { data: newDossier, error: dossierError } = await supabase
        .from('dossiers')
        .insert([{
          user_id: user.id,
          name: dossierName,
          contexte: this.searchQuery,
          created_at: new Date().toISOString(),
          is_analysing: 'pending',
          MODE: 'FLASH'  // Important: marquer comme FLASH
        }])
        .select();

      if (dossierError) throw dossierError;

      const dossierId = newDossier[0].id;

      // Call backend to start flash query
      const token = await supabase.auth.getSession();
      const authToken = token.data.session?.access_token;

      const response = await fetch(`${API_URL}/copilot/start-query-flash`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
          'X-Country': this.country,
        },
        body: JSON.stringify({
          userId: user.id,
          dossierId: dossierId,
          question: this.searchQuery,
          selected_sources: this.selectedSourceIds,
          country: this.country,
          ...(isFirstSearch && { first_search: true }),
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || this.$t('dashboard.errors.flashQueryLaunch'));
      }

      // Redirect to dossier page
      this.$router.push(`/dossier/${dossierId}`);

      // Reset search
      this.searchQuery = '';

    } catch (error) {
      console.error('Error starting flash query:', error);
      this.$toast.error(this.$t('errors.generic', { message: error.message }));
    } finally {
      this.isSubmittingFlash = false;
    }
  },

    async handleAgentQuery() {
      if (this.isSubmittingAgent || this.isSubmittingFlash || this.isSubmittingSearch || this.isSubmittingTableau) {
        return;
      }

      if (!this.searchQuery.trim()) {
        alert(this.$t('validation.searchRequired'));
        return;
      }
      if (this.selectedSourcesCount === 0) {
        this.$toast.warning(this.$t('validation.sourceRequired'));
        return;
      }
      if (!(await this.ensureSubscriptionAllows())) return;

      this.isSubmittingAgent = true;

      // Chip Rédacteur actif : l'agent rédige dans ce modèle (template_id dans
      // le body de start-query-agent) ; le dossier reste un dossier agent
      // normal. Le chip est consommé après un envoi réussi.
      const templateChip = this.selectedTemplateChip;

      // Détection 1ère recherche (historique local vide AVANT l'insert du nouveau dossier).
      // Backend re-vérifie via COUNT — ici on envoie juste le hint.
      const isFirstSearch = (this.dossiers?.length || 0) === 0;

      try {
        const user = await getCurrentUser();
        if (!user) throw new Error('User not authenticated');

        const dossierName = this.searchQuery.length > 30
          ? this.searchQuery.substring(0, 30) + '...'
          : this.searchQuery;

        const { data: newDossier, error: dossierError } = await supabase
          .from('dossiers')
          .insert([{
            user_id: user.id,
            name: dossierName,
            contexte: this.searchQuery,
            created_at: new Date().toISOString(),
            is_analysing: 'pending',
            MODE: 'AGENT'
          }])
          .select();

        if (dossierError) throw dossierError;

        const dossierId = newDossier[0].id;

        const token = await supabase.auth.getSession();
        const authToken = token.data.session?.access_token;

        const response = await fetch(`${API_URL}/copilot/start-query-agent`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
            'X-Country': this.country,
          },
          body: JSON.stringify({
            userId: user.id,
            dossierId: dossierId,
            question: this.searchQuery,
            country: this.country,
            ...(templateChip ? { template_id: templateChip.id } : {}),
            ...(this.selectedWorkspaceId ? { workspace_id: this.selectedWorkspaceId } : {}),
            ...(this.selectedWorkspaceId && this.selectedWorkspaceScope && this.selectedWorkspaceScope.length
              ? { workspace_scope: this.selectedWorkspaceScope }
              : {}),
            ...(isFirstSearch && { first_search: true }),
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || this.$t('dashboard.errors.agentQueryLaunch'));
        }

        // Envoi réussi : le chip modèle est consommé
        if (templateChip) this.selectedTemplateChip = null;

        this.$router.push(`/agent/${dossierId}`);
        this.searchQuery = '';

      } catch (error) {
        console.error('Error starting agent query:', error);
        this.$toast.error(this.$t('errors.generic', { message: error.message }));
      } finally {
        this.isSubmittingAgent = false;
      }
    },

    async openTableauModal() {
      if (!this.searchQuery.trim()) {
        alert(this.$t('validation.searchRequired'));
        return;
      }
      if (this.selectedSourcesCount === 0) {
        this.$toast.warning(this.$t('validation.sourceRequired'));
        return;
      }
      if (!(await this.ensureSubscriptionAllows())) return;
      this.tableauSubQuestions = [];
      this.tableauSubQuestionsLoading = true;
      this.showTableauModal = true;

      try {
        const token = await supabase.auth.getSession();
        const authToken = token.data.session?.access_token;
        const response = await fetch(`${API_URL}/copilot/generate-tableau-sub-questions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({ question: this.searchQuery.trim() })
        });
        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.error || this.$t('dashboard.errors.generationFailed'));
        }
        const data = await response.json();
        this.tableauSubQuestions = (data.sub_questions || []).filter(Boolean);
        if (this.tableauSubQuestions.length === 0) {
          this.tableauSubQuestions = [
            this.$t('dashboard.tableau.defaultQuestions.jurisdiction'),
            this.$t('dashboard.tableau.defaultQuestions.date'),
            this.$t('dashboard.tableau.defaultQuestions.basis'),
            this.$t('dashboard.tableau.defaultQuestions.solution')
          ];
        }
      } catch (e) {
        console.error('Generate sub-questions:', e);
        this.$toast.error(this.$t('dashboard.toasts.subQuestionsError', { message: e.message }));
        this.tableauSubQuestions = [
          this.$t('dashboard.tableau.defaultQuestions.jurisdiction'),
          this.$t('dashboard.tableau.defaultQuestions.date'),
          this.$t('dashboard.tableau.defaultQuestions.basis'),
          this.$t('dashboard.tableau.defaultQuestions.solution')
        ];
      } finally {
        this.tableauSubQuestionsLoading = false;
        this.$nextTick(() => this.resizeAllTableauTextareas());
      }
    },

    resizeAllTableauTextareas() {
      this.$nextTick(() => {
        this.$el.querySelectorAll?.('.tableau-sq-input')?.forEach(el => {
          el.style.height = 'auto';
          el.style.height = Math.min(el.scrollHeight, 200) + 'px';
        });
      });
    },

    closeTableauModal() {
      this.showTableauModal = false;
      this.tableauSubQuestionsLoading = false;
    },

    async openImproveModal() {
      if (!this.searchQuery.trim()) {
        alert(this.$t('validation.searchRequired'));
        return;
      }
      this.improveOriginalQuestion = this.searchQuery.trim();
      this.improveQuestions = [];
      this.improveAnswers = [];
      this.improvedQuestion = '';
      this.improveStep = 'loading';
      this.showImproveModal = true;

      try {
        const data = await this.callImproveApi('questions');
        if (!this.showImproveModal) return; // fermée entre-temps
        const questions = (data.questions || []).filter(Boolean);
        if (questions.length === 0) {
          // Question déjà précise : réécriture directe sans clarification
          await this.requestImprovedQuestion();
        } else {
          this.improveQuestions = questions;
          this.improveAnswers = questions.map(() => '');
          this.improveStep = 'questions';
        }
      } catch (e) {
        console.error('Improve question:', e);
        this.$toast.error(this.$t('dashboard.improve.error'));
        this.closeImproveModal();
      }
    },

    async callImproveApi(step) {
      const token = await supabase.auth.getSession();
      const authToken = token.data.session?.access_token;
      const body = {
        step,
        question: this.improveOriginalQuestion,
        country: this.country,
        sources: this.availableSources.filter(s => s.selected).map(s => s.title),
      };
      // Workspace sélectionné : la baguette reçoit l'arborescence + le résumé
      // du dossier (et le périmètre coché) pour ancrer questions et réécriture.
      if (this.selectedWorkspaceId) {
        body.workspace_id = this.selectedWorkspaceId;
        if (this.selectedWorkspaceScope && this.selectedWorkspaceScope.length) {
          body.workspace_scope = this.selectedWorkspaceScope;
        }
      }
      if (step === 'rewrite') {
        body.clarifications = this.improveQuestions.map((q, idx) => ({
          question: q,
          answer: (this.improveAnswers[idx] || '').trim(),
        }));
      }
      const response = await fetch(`${API_URL}/copilot/improve-question`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
          'X-Country': this.country,
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || this.$t('dashboard.improve.error'));
      }
      return response.json();
    },

    onImproveAnswerInput(ev, idx) {
      // $set : l'assignation par index n'est pas réactive en Vue 2
      // (nécessaire pour le liseré « répondu » de la carte)
      this.$set(this.improveAnswers, idx, ev.target.value);
      this.autoResizeTextarea(ev);
    },

    async submitImproveAnswers() {
      try {
        await this.requestImprovedQuestion();
      } catch (e) {
        console.error('Improve question (rewrite):', e);
        this.$toast.error(this.$t('dashboard.improve.error'));
        this.closeImproveModal();
      }
    },

    async requestImprovedQuestion() {
      this.improveStep = 'rewriting';
      const data = await this.callImproveApi('rewrite');
      if (!this.showImproveModal) return;
      this.improvedQuestion = data.improved_question || this.improveOriginalQuestion;
      this.improveStep = 'result';
      this.$nextTick(() => {
        this.$el.querySelectorAll?.('.improve-result-input')?.forEach(el => {
          el.style.height = 'auto';
          el.style.height = Math.min(el.scrollHeight, 300) + 'px';
        });
      });
    },

    applyImprovedQuestion() {
      const improved = this.improvedQuestion.trim();
      if (improved) {
        this.searchQuery = improved;
        this.$nextTick(() => this.adjustTextareaHeight());
      }
      this.closeImproveModal();
    },

    closeImproveModal() {
      this.showImproveModal = false;
      this.improveStep = 'loading';
    },

    autoResizeTextarea(ev) {
      const el = ev?.target;
      if (!el || el.tagName !== 'TEXTAREA') return;
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 200) + 'px';
    },

    async handleTableauQuery() {
      const validQuestions = this.tableauSubQuestions.filter(s => s.trim());
      if (validQuestions.length === 0) {
        this.$toast.warning(this.$t('dashboard.toasts.addAtLeastOneColumn'));
        return;
      }

      this.isSubmittingTableau = true;

      try {
        const user = await getCurrentUser();
        if (!user) throw new Error('User not authenticated');

        const dossierName = this.searchQuery.length > 30
          ? this.searchQuery.substring(0, 30) + '...'
          : this.searchQuery;

        const { data: newDossier, error: dossierError } = await supabase
          .from('dossiers')
          .insert([{
            user_id: user.id,
            name: dossierName,
            contexte: this.searchQuery,
            created_at: new Date().toISOString(),
            is_analysing: 'pending',
            MODE: 'TABLEAU'
          }])
          .select();

        if (dossierError) throw dossierError;

        const dossierId = newDossier[0].id;

        const token = await supabase.auth.getSession();
        const authToken = token.data.session?.access_token;

        const response = await fetch(`${API_URL}/copilot/start-query-tableau`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            userId: user.id,
            dossierId: dossierId,
            question: this.searchQuery,
            sub_questions: validQuestions
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || this.$t('dashboard.errors.tableauQueryLaunch'));
        }

        this.closeTableauModal();
        this.$router.push(`/dossier/${dossierId}`);
        this.searchQuery = '';

      } catch (error) {
        console.error('Error starting tableau query:', error);
        this.$toast.error(this.$t('errors.generic', { message: error.message }));
      } finally {
        this.isSubmittingTableau = false;
      }
    },

    openInfoModal(dossier) {
      this.selectedDossierInfo = dossier.contexte || dossier.name || this.$t('dashboard.infoModal.noInfo');
      this.showInfoModal = true;
    },

    closeInfoModal() {
      this.showInfoModal = false;
      this.selectedDossierInfo = '';
    },

    formatDossierDate(dateString) {
      if (!dateString) return '';

      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      // Si c'est aujourd'hui
      if (diffDays === 0) {
        return this.$t('dashboard.date.today');
      }

      // Si c'est hier
      if (diffDays === 1) {
        return this.$t('dashboard.date.yesterday');
      }

      // Si moins d'une semaine
      if (diffDays < 7) {
        return this.$t('dashboard.date.daysAgo', { days: diffDays });
      }

      // Sinon, afficher la date courte
      return this.$localeDate(date, {
        day: 'numeric',
        month: 'short'
      });
    },

    dossierModeLabel(mode) {
      if (mode === 'FLASH') return this.$t('dashboard.modes.fast');
      if (mode === 'AGENT') return this.$t('dashboard.modes.agent');
      if (mode === 'TABLEAU') return this.$t('dashboard.modes.tableau');
      return this.$t('dashboard.modes.long');
    },

    dossierModeIconPath(mode) {
      if (mode === 'FLASH') {
        return 'M11.251.068a.5.5 0 0 1 .227.58L9.677 6.5H13a.5.5 0 0 1 .364.843l-8 8.5a.5.5 0 0 1-.842-.49L6.323 9.5H3a.5.5 0 0 1-.364-.843l8-8.5a.5.5 0 0 1 .615-.09z';
      }
      if (mode === 'AGENT') {
        return 'M5.68 5.792 7.345 7.75 5.68 9.708a2.75 2.75 0 1 1 0-3.916ZM8 6.978 6.416 5.113l-.014-.015a3.75 3.75 0 1 0 0 5.304l.014-.015L8 8.522l1.586 1.865.014.015a3.75 3.75 0 1 0 0-5.304l-.014.015zm.656.772 1.663-1.958a2.75 2.75 0 1 1 0 3.916z';
      }
      if (mode === 'TABLEAU') {
        return 'M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm15 2h-4v3h4zm0 4h-4v3h4zm0 4h-4v3h3a1 1 0 0 0 1-1zm-5 3v-3H6v3zm-5 0v-3H1v2a1 1 0 0 0 1 1zm-4-4h4V8H1zm0-4h4V4H1zm5-3v3h4V4zm4 4H6v3h4z';
      }
      return 'M14 1a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H4.414A2 2 0 0 0 3 11.586l-2 2V2a1 1 0 0 1 1-1zM2 0a2 2 0 0 0-2 2v12.793a.5.5 0 0 0 .854.353l2.853-2.853A1 1 0 0 1 4.414 12H14a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2z';
    },

    // NOUVELLES méthodes helper
    getGroupDossiers(groupId) {
      return this.dossiers.filter(d => d.group_id === groupId);
    },

    getGroupDossierCount(groupId) {
      return this.dossiers.filter(d => d.group_id === groupId).length;
    },

    goBackFromGroupView() {
      this.expandedGroupId = null;
      this.dossierSearchQuery = '';
      history.pushState({ expandedGroupId: null }, '', window.location.pathname);
    },

    toggleShowAllDossiers() {
      this.showAllDossiers = !this.showAllDossiers;
    },

    toggleGroupMenu(groupId) {
      this.openGroupMenuId = this.openGroupMenuId === groupId ? null : groupId;
    },

    handlePopState(event) {
      const state = event.state;
      if (state && state.expandedGroupId) {
        this.expandedGroupId = state.expandedGroupId;
        this.loadAllGroupDossiers(state.expandedGroupId);
      } else {
        this.expandedGroupId = null;
      }
    },

    startRename(item) {
      this.itemToRename = item;
      this.showRenameModal = true;
      this.openMenuId = null;
      this.openGroupMenuId = null;
    },

    cancelRename() {
      this.showRenameModal = false;
      this.itemToRename = null;
    },

    async confirmRename(newName) {
      if (!this.itemToRename) return;

      const { id, type } = this.itemToRename;
      const isGroup = type === 'group';
      const tableName = isGroup ? 'groups' : 'dossiers';

      try {
        const { error } = await supabase
          .from(tableName)
          .update({ name: newName })
          .eq('id', id);

        if (error) throw error;

        const list = isGroup ? this.groups : this.dossiers;
        const itemInList = list.find(i => i.id === id);
        if (itemInList) {
          itemInList.name = newName;
        }

      } catch (error) {
        console.error(`Error renaming ${type}:`, error);
        this.$toast.error(this.$t('dashboard.toasts.renameError'));
      } finally {
        this.cancelRename();
      }
    },

    async togglePinDossier(dossier) {
      const newPinnedState = !dossier.is_pinned;
      const originalState = dossier.is_pinned;

      // Optimistic update
      dossier.is_pinned = newPinnedState;

      try {
        const token = await getSupabaseToken();
        const response = await axios.post(
          `${API_URL}/copilot/toggle-pin-dossier`,
          {
            dossierId: dossier.id,
            isPinned: newPinnedState
          },
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (!response.data.success) {
          throw new Error('Failed to toggle pin');
        }

      } catch (error) {
        console.error('Error toggling pin:', error);
        // Revert optimistic update
        dossier.is_pinned = originalState;
        this.$toast.error(this.$t('dashboard.toasts.pinError'));
      }
    },

    async onSearchFocus() {
      if (!this.allDossiersLoaded && !this.isLoadingAllDossiers) {
        await this.loadAllDossiersWithFocusRestore();
      }
    },

    async loadAllDossiersWithFocusRestore() {
      this.isLoadingAllDossiers = true;

      try {
        const user = await getCurrentUser();
        if (!user) throw new Error('User not authenticated');

        const { data: allDossiers, error } = await supabase
          .from('dossiers')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (error) throw error;

        this.dossiers = allDossiers || [];
        this.allDossiersLoaded = true;
        this.hasMoreDossiers = false;

      } catch (error) {
        console.error('Error loading all dossiers:', error);
        this.$toast.error(this.$t('dashboard.toasts.loadAllDossiersError'));
      } finally {
        this.isLoadingAllDossiers = false;

        this.$nextTick(() => {
          const searchInput = this.$el.querySelector('.dossier-search-input');
          if (searchInput) {
            searchInput.focus();
            const length = searchInput.value.length;
            searchInput.setSelectionRange(length, length);
          }
        });
      }
    },

    startDeleteGroup(group) {
        this.groupToDelete = group;
        this.openGroupMenuId = null;
    },

    cancelDeleteGroup() {
        this.groupToDelete = null;
    },

    async confirmDeleteGroup({ deleteContent }) {
        if (!this.groupToDelete) return;

        const groupId = this.groupToDelete.id;
        const searchIdsToDelete = this.groupToDelete.dossiers.map(d => d.id);

        const originalGroups = [...this.groups];
        const originalDossiers = [...this.dossiers];

        this.groups = this.groups.filter(g => g.id !== groupId);
        if (deleteContent) {
            this.dossiers = this.dossiers.filter(d => !searchIdsToDelete.includes(d.id));
        } else {
            this.dossiers.forEach(d => {
                if (d.group_id === groupId) {
                    d.group_id = null;
                }
            });
        }

        this.cancelDeleteGroup();

        try {
            if (deleteContent) {
                if (searchIdsToDelete.length > 0) {
                    const { error: dossiersError } = await supabase.from('dossiers').delete().in('id', searchIdsToDelete);
                    if (dossiersError) throw dossiersError;
                }
            } else {
                 if (searchIdsToDelete.length > 0) {
                    const { error: dossiersError } = await supabase.from('dossiers').update({ group_id: null }).in('id', searchIdsToDelete);
                    if (dossiersError) throw dossiersError;
                }
            }

            const { error: groupError } = await supabase.from('groups').delete().eq('id', groupId);
            if (groupError) throw groupError;

        } catch (error) {
            console.error('Error deleting group:', error);
            this.$toast.error(this.$t('dashboard.toasts.deleteGroupError'));
            this.groups = originalGroups;
            this.dossiers = originalDossiers;
        }
    },

    handleClickOutside(event) {
      if (this.openMenuId !== null && !event.target.closest('.more-icon') && !event.target.closest('.dossier-menu')) {
        this.openMenuId = null;
      }
      if (this.openGroupMenuId !== null && !event.target.closest('.more-icon') && !event.target.closest('.dossier-menu')) {
        this.openGroupMenuId = null;
      }
      if (this.showGroupAssignmentMenu !== null && !event.target.closest('.more-icon') && !event.target.closest('.dossier-menu')) {
        this.showGroupAssignmentMenu = null;
      }
      // Close sources dropdown when clicking outside
      if (this.showSourcesDropdown && !event.target.closest('.sources-dropdown-container')) {
        this.showSourcesDropdown = false;
      }
      // Close the « + » popover when clicking outside
      if (this.showPlusMenu && !event.target.closest('.sources-dropdown-container')) {
        this.showPlusMenu = false;
      }
      // Close mode dropdown when clicking outside
      if (this.showModeDropdown && !event.target.closest('.mode-selector-container')) {
        this.showModeDropdown = false;
      }
      // Close workspace dropdown when clicking outside
      if (this.showWorkspaceDropdown && !event.target.closest('.workspace-selector-container')) {
        this.showWorkspaceDropdown = false;
      }
    },

    async expandGroup(groupId) {
      this.expandedGroupId = groupId;
      history.pushState({ expandedGroupId: groupId }, '', `?group=${groupId}`);
      await this.loadAllGroupDossiers(groupId);
    },

    async loadAllGroupDossiers(groupId) {
      if (!groupId || groupId === 'ungrouped') return;

      this.loadingGroupDossiers = true;

      try {
        const user = await getCurrentUser();
        if (!user) throw new Error('User not authenticated');

        const { data: groupDossiers, error } = await supabase
          .from('dossiers')
          .select('*')
          .eq('user_id', user.id)
          .eq('group_id', groupId)
          .order('created_at', { ascending: false });

        if (error) throw error;

        const existingIds = new Set(this.dossiers.map(d => d.id));
        const newDossiers = groupDossiers.filter(d => !existingIds.has(d.id));

        if (newDossiers.length > 0) {
          this.dossiers = [...this.dossiers, ...newDossiers];
        }

      } catch (error) {
        console.error('Error loading group dossiers:', error);
        this.$toast.error(this.$t('dashboard.toasts.loadGroupDossiersError'));
      } finally {
        this.loadingGroupDossiers = false;
      }
    },

    async loadAllDossiersWithFocus() {
      if (this.allDossiersLoaded || this.isLoadingAllDossiers) {
        return;
      }

      const searchInput = this.$el.querySelector('.dossier-search-input');
      const cursorPosition = searchInput ? searchInput.selectionStart : 0;
      const searchValue = this.dossierSearchQuery;

      await this.loadAllDossiers();

      this.$nextTick(() => {
        const newSearchInput = this.$el.querySelector('.dossier-search-input');
        if (newSearchInput) {
          newSearchInput.focus();
          newSearchInput.setSelectionRange(cursorPosition, cursorPosition);
          if (newSearchInput.value !== searchValue) {
            newSearchInput.value = searchValue;
          }
        }
      });
    },

    async loadAllDossiers() {
      if (this.allDossiersLoaded || this.isLoadingAllDossiers) {
        return;
      }

      this.isLoadingAllDossiers = true;

      try {
        const user = await getCurrentUser();
        if (!user) throw new Error('User not authenticated');

        const { data: allDossiers, error } = await supabase
          .from('dossiers')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (error) throw error;

        this.dossiers = allDossiers || [];
        this.allDossiersLoaded = true;
        this.hasMoreDossiers = false;

      } catch (error) {
        console.error('Error loading all dossiers:', error);
        this.$toast.error(this.$t('dashboard.toasts.loadAllDossiersError'));
      } finally {
        this.isLoadingAllDossiers = false;
      }
    },

    async fetchDossiersAndGroups(page = 0, append = false) {
      if (!append) {
        this.isLoadingDossiers = true;
        this.allDossiersLoaded = false;
      }

      try {
        const user = await getCurrentUser();
        if (!user) throw new Error('User not authenticated');

        const from = page * this.dossiersPerPage;
        const to = from + this.dossiersPerPage - 1;

        // Page 0: charger aussi les dossiers épinglés pour qu'ils apparaissent en premier même hors des 10 premiers
        const pinnedQuery = page === 0 && !append
          ? supabase
              .from('dossiers')
              .select('*')
              .eq('user_id', user.id)
              .eq('is_pinned', true)
          : null;

        const [dossierRes, groupRes, pinnedRes] = await Promise.all([
          safeRangeQuery(
            supabase
              .from('dossiers')
              .select('*')
              .eq('user_id', user.id)
              .order('created_at', { ascending: false }),
            from, to
          ),
          page === 0 ? supabase
            .from('groups')
            .select('*')
            .eq('user_id', user.id) : Promise.resolve({ data: this.groups, error: null }),
          pinnedQuery ? pinnedQuery : Promise.resolve({ data: [], error: null })
        ]);

        if (dossierRes.error) throw dossierRes.error;
        if (groupRes.error) throw groupRes.error;
        if (pinnedRes?.error) throw pinnedRes.error;

        const newDossiers = dossierRes.data || [];
        const pinnedDossiers = (pinnedRes?.data || []).filter(Boolean);

        if (append) {
          this.dossiers = [...this.dossiers, ...newDossiers];
        } else {
          // Fusionner les épinglés (toujours visibles en premier) avec la première page de non-épinglés
          const pinnedIds = new Set(pinnedDossiers.map(d => d.id));
          const unpinnedFromFirstPage = newDossiers.filter(d => !pinnedIds.has(d.id));
          this.dossiers = [...pinnedDossiers, ...unpinnedFromFirstPage];
          this.groups = groupRes.data || [];
        }

        this.hasMoreDossiers = newDossiers.length === this.dossiersPerPage;
        this.dossiersPage = page;

      } catch (error) {
        console.error('Error fetching data:', error);
        this.$toast.error(this.$t('dashboard.toasts.fetchDataError'));
      } finally {
        this.isLoadingDossiers = false;
        this.loadingMoreDossiers = false;
      }
    },


    async loadMoreDossiers() {
      if (this.loadingMoreDossiers || !this.hasMoreDossiers) return;
      this.loadingMoreDossiers = true;
      await this.fetchDossiersAndGroups(this.dossiersPage + 1, true);
    },

    openGroupAssignmentMenu(dossier) {
      this.openMenuId = null;
      this.showGroupAssignmentMenu = dossier.id;
    },

    openCreateGroupModal() {
      this.showCreateGroupModal = true;
    },
    closeCreateGroupModal() {
      this.showCreateGroupModal = false;
    },

    async confirmCreateGroup(groupName) {
      this.closeCreateGroupModal();
      try {
        const user = await getCurrentUser();
        if (!user) {
          throw new Error("User not authenticated");
        }
        const response = await supabase
          .from('groups')
          .insert({ name: groupName, user_id: user.id })
          .select()
          .single();

        if (response.error) {
          throw response.error;
        }

        const newGroup = response.data;
        this.groups.push(newGroup);

      } catch (error) {
        console.error("Error creating group:", error);
        this.$toast.error(this.$t('dashboard.toasts.createGroupError'));
      }
    },

    async assignDossierToGroup(dossier, groupId) {
      const originalGroupId = dossier.group_id;
      this.showGroupAssignmentMenu = null;

      const dossierInState = this.dossiers.find(d => d.id === dossier.id);

      if (dossierInState) {
        dossierInState.group_id = groupId;
      }

      try {
        const { error } = await supabase
          .from('dossiers')
          .update({ group_id: groupId })
          .eq('id', dossier.id);

        if (error) {
          if (dossierInState) {
              dossierInState.group_id = originalGroupId;
          }
          this.$toast.error(this.$t('dashboard.toasts.moveDossierError'));
          throw error;
        }
      } catch (error) {
        console.error("Error assigning dossier:", error);
      }
    },

    toggleDossierMenu(dossierId) {
      const isOpen = this.openMenuId === dossierId || this.showGroupAssignmentMenu === dossierId;
      if (isOpen) {
        this.openMenuId = null;
        this.showGroupAssignmentMenu = null;
      } else {
        this.openMenuId = dossierId;
        this.showGroupAssignmentMenu = null;
      }
    },

    async refreshSubscription() {
      try {
        this.sub = await getSubscription();
      } catch (err) {
        console.error('❌ Error fetching subscription:', err);
        this.sub = { plan: "none", status: "none", queries_used: 0, current_period_end: null };
      }
    },

    // Autorisation d'une recherche : le backend est la source de vérité. On
    // rafraîchit le statut d'abonnement EN DIRECT juste avant de décider, pour
    // ne jamais bloquer à tort un client dont le `sub` en cache serait périmé
    // (ex : paiement effectué après le chargement de la page → la fenêtre
    // "il faut payer" ne se rafraîchissait pas). La Cloud Function re-vérifie
    // de toute façon côté serveur (402) à chaque recherche.
    async ensureSubscriptionAllows() {
      await this.refreshSubscription();
      if (this.canRequest) return true;
      if (this.sub.plan === 'student' && this.quotaRemaining <= 0) {
        this.showQuotaExceededModal = true;
      } else {
        this.showPaywall = true;
      }
      return false;
    },

    upgrade() {
      // Self-checkout retiré : la conversion passe désormais par un conseiller (sales-led).
      // On redirige vers la page de contact au lieu d'ouvrir Stripe Checkout.
      this.navigateToContact();
    },

    async requestTrial() {
      this.showPaywall = false;
      this.$toast.info(this.$t('dashboard.toasts.salesContact'));
    },

    closePaywallModal() {
      this.showPaywall = false;
    },

    navigateToTrial() {
      this.showPaywall = false;
      this.$router.push('/trialPage');
    },

    navigateToStudent() {
      this.showPaywall = false;
      this.$router.push('/studentPage');
    },

    navigateToContact() {
      this.showPaywall = false;
      this.$router.push('/contactPage');
    },

    adjustTextareaHeight() {
      const textarea = this.$refs.searchBox;
      if (!textarea) return;

      textarea.style.height = "auto";
      textarea.style.height = `${Math.min(textarea.scrollHeight, 300)}px`;
    },

    adjustRefinementHeight() {
      const textarea = this.$refs.refinementTextarea;
      if (!textarea) return;

      textarea.style.height = "auto";
      textarea.style.height = `${Math.min(textarea.scrollHeight, 300)}px`;
    },

    // ========== SOURCES SELECTION METHODS ==========
    toggleSourcesDropdown() {
      this.showSourcesDropdown = !this.showSourcesDropdown;
      if (this.showSourcesDropdown) {
        this.showWorkspaceDropdown = false;
        this.showModeDropdown = false;
        this.showPlusMenu = false;
      }
    },
    toggleModeDropdown() {
      this.showModeDropdown = !this.showModeDropdown;
      if (this.showModeDropdown) {
        this.showWorkspaceDropdown = false;
        this.showSourcesDropdown = false;
        this.showPlusMenu = false;
      }
    },

    // ========== BOUTON « + » (Sources / Rédiger) ==========
    togglePlusMenu() {
      this.showPlusMenu = !this.showPlusMenu;
      if (this.showPlusMenu) {
        this.showSourcesDropdown = false;
        this.showWorkspaceDropdown = false;
        this.showModeDropdown = false;
      }
    },
    handleGlobalKeydown(event) {
      if (event.key === 'Escape' && this.showPlusMenu) {
        this.showPlusMenu = false;
      }
    },
    openSourcesFromPlusMenu() {
      // Reprend EXACTEMENT le comportement de l'ancien bouton Sources :
      // même état showSourcesDropdown, même dropdown, même ancrage
      // (.sources-dropdown-container). Le popover se referme.
      if (this.selectedQueryMode === 'TABLEAU' || this.selectedWorkspaceId !== null) return;
      this.showPlusMenu = false;
      this.toggleSourcesDropdown();
    },
    openTemplateManagerFromPlusMenu() {
      this.showPlusMenu = false;
      this.showTemplateManager = true;
    },

    // ========== RÉDACTEUR (chip modèle de rédaction) ==========
    // Le chip ne redirige plus vers /redacteur : il marque l'envoi suivant
    // comme une rédaction dans ce modèle. L'envoi suit le flux dossier agent
    // normal (handleAgentQuery, template_id dans le body).
    onTemplateUse({ templateId, templateName, skillId }) {
      this.selectedTemplateChip = { id: templateId, name: templateName, skillId };
      this.showTemplateManager = false;
    },
    clearTemplateChip() {
      this.selectedTemplateChip = null;
    },

    selectAllSources() {
      this.availableSources.forEach(s => s.selected = true);
    },

    deselectAllSources() {
      this.availableSources.forEach(s => s.selected = false);
    },

    closeSourcesDropdown(event) {
      if (this.showSourcesDropdown && !event.target.closest('.sources-dropdown-container')) {
        this.showSourcesDropdown = false;
      }
    },

    // ========== SPEECH-TO-TEXT METHODS ==========
    async toggleRecording() {
      if (this.isRecording) {
        this.stopRecording();
      } else {
        await this.startRecording();
      }
    },

    async startRecording() {
      try {
        // Request microphone permission
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            sampleRate: 44100
          }
        });

        // Determine the best supported MIME type
        const mimeType = this.getSupportedMimeType();

        this.mediaRecorder = new MediaRecorder(stream, { mimeType });
        this.audioChunks = [];

        this.mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            this.audioChunks.push(event.data);
          }
        };

        this.mediaRecorder.onstop = async () => {
          // Stop all tracks to release the microphone
          stream.getTracks().forEach(track => track.stop());

          // Process the recording
          await this.processRecording();
        };

        this.mediaRecorder.start();
        this.isRecording = true;

      } catch (error) {
        console.error('Error starting recording:', error);

        if (error.name === 'NotAllowedError') {
          this.$toast.error(this.$t('dashboard.toasts.micAccessDenied'));
        } else if (error.name === 'NotFoundError') {
          this.$toast.error(this.$t('dashboard.toasts.micNotFound'));
        } else {
          this.$toast.error(this.$t('dashboard.toasts.recordingStartError'));
        }
      }
    },

    getSupportedMimeType() {
      const types = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/ogg;codecs=opus',
        'audio/wav'
      ];

      for (const type of types) {
        if (MediaRecorder.isTypeSupported(type)) {
          return type;
        }
      }
      return 'audio/webm'; // Fallback
    },

    stopRecording() {
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop();
        this.isRecording = false;
      }
    },

    async processRecording() {
      if (this.audioChunks.length === 0) {
        this.$toast.warning(this.$t('dashboard.toasts.noAudioRecorded'));
        return;
      }

      this.isTranscribing = true;

      try {
        // Create blob from chunks
        const mimeType = this.mediaRecorder?.mimeType || 'audio/webm';
        const audioBlob = new Blob(this.audioChunks, { type: mimeType });

        // Check if the recording is too short
        if (audioBlob.size < 1000) {
          this.$toast.warning(this.$t('dashboard.toasts.recordingTooShort'));
          return;
        }

        // Determine file extension from MIME type
        let extension = 'webm';
        if (mimeType.includes('mp4')) extension = 'mp4';
        else if (mimeType.includes('ogg')) extension = 'ogg';
        else if (mimeType.includes('wav')) extension = 'wav';

        // Get auth token
        const { data } = await supabase.auth.getSession();
        const token = data.session?.access_token;

        if (!token) {
          this.$toast.error(this.$t('errors.unauthorized'));
          return;
        }

        // Encode audio as base64 to avoid Cloudflare WAF blocking binary multipart payloads
        const base64Audio = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(audioBlob);
        });

        // Send to backend
        const response = await fetch(`${API_URL}/copilot/speech-to-text`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            audio: base64Audio,
            mimeType: mimeType,
            filename: `recording.${extension}`,
            language: 'fr'
          })
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error || 'Transcription failed');
        }

        if (result.text) {
          // Append to existing search query (or replace if empty)
          if (this.searchQuery.trim()) {
            this.searchQuery += ' ' + result.text;
          } else {
            this.searchQuery = result.text;
          }

          // Adjust textarea height
          this.$nextTick(() => {
            this.adjustTextareaHeight();
            // Focus the textarea
            if (this.$refs.searchBox) {
              this.$refs.searchBox.focus();
            }
          });

          this.$toast.success(this.$t('dashboard.toasts.transcriptionSuccess'));
        } else {
          this.$toast.warning(this.$t('dashboard.toasts.noTextDetected'));
        }

      } catch (error) {
        console.error('Error transcribing audio:', error);
        this.$toast.error(this.$t('errors.generic', { message: error.message }));
      } finally {
        this.isTranscribing = false;
        this.audioChunks = [];
      }
    },

    async handleSearchSubmit() {
      // Guard contre les double-soumissions
      if (this.isSubmittingSearch || this.isSubmittingFlash || this.isSubmittingAgent || this.isSubmittingTableau) {
        return;
      }

      // Chip Rédacteur actif : le chip implique le mode agent, quel que soit
      // le mode sélectionné : l'envoi suit le flux dossier agent normal
      // (handleAgentQuery porte le template_id et consomme le chip).
      if (this.selectedTemplateChip) {
        this.handleAgentQuery();
        return;
      }

      if (!this.searchQuery.trim()) {
        alert(this.$t('validation.searchRequired'));
        return;
      }

      if (this.selectedSourcesCount === 0) {
        this.$toast.warning(this.$t('validation.sourceRequired'));
        return;
      }

      if (this.searchQuery.length > 50000) {
        alert(this.$t('dashboard.errors.queryTooLong'));
        return;
      }

      // Pour les utilisateurs TABLEAU / WORKSPACE / FUTUR, dispatcher selon le mode sélectionné
      if (this.isAgentUser) {
        if (this.selectedQueryMode === 'AGENT') {
          this.handleAgentQuery();
          return;
        }
        if (this.selectedQueryMode === 'FLASH') {
          this.handleFlashQuery();
          return;
        }
        if (this.selectedQueryMode === 'TABLEAU') {
          this.openTableauModal();
          return;
        }
        // selectedQueryMode === null → Réponse longue standard, on continue
      }

      if (!(await this.ensureSubscriptionAllows())) return;
      this.isSubmittingSearch = true;
      this.isSearchActive = true;
      this.fetchPoints();
    },

    closeQuotaExceededModal() {
      this.showQuotaExceededModal = false;
    },


    cancelSearch() {
      this.isSearchActive = false;
      this.pointsList = [];
      this.isLoadingPoints = false;
      this.sourceUnavailable = false;
    },

    // eslint-disable-next-line no-unused-vars
    openDossier(dossier, index) {
      // Ouvrir un dossier existant = simple navigation, jamais gaté par
      // l'abonnement (et surtout pas par un statut en cache périmé).
      if (dossier.MODE === 'AGENT') {
        this.$router.push(`/agent/${dossier.id}`);
      } else {
        this.$router.push(`/dossier/${dossier.id}`);
      }
    },

    deleteDossier(dossier) {
      this.dossierToDelete = dossier;
      this.showDeleteModal = true;
      this.openMenuId = null;
    },

    async confirmDeleteDossier() {
      if (!this.dossierToDelete) return;
      try {
        const user = await getCurrentUser();
        const dossierId = this.dossierToDelete.id;

        const { error: storageError } = await supabase.storage
          .from('dossiers')
          .remove([`analyses/${dossierId}.json`]);

        if (storageError) {
          // Storage cleanup optional - continue with DB delete
        }

        const { error: dbError } = await supabase
          .rpc('delete_dossier_cascade', {
            dossier_id: dossierId,
            user_id: user.id
          });

        if (dbError) {
          const { error: standardDeleteError } = await supabase
            .from('dossiers')
            .delete()
            .eq('id', dossierId)
            .eq('user_id', user.id);

          if (standardDeleteError) throw standardDeleteError;
        }

        this.dossiers = this.dossiers.filter(d => d.id !== dossierId);

        this.dossierToDelete = null;
        this.showDeleteModal = false;

      } catch (error) {
        console.error('Error deleting dossier:', error);
        alert(this.$t('dashboard.errors.deleteDossier'));
        this.dossierToDelete = null;
        this.showDeleteModal = false;
      }
    },

    cancelDeleteDossier() {
      this.dossierToDelete = null;
      this.showDeleteModal = false;
    },

    goToExamplePage() {
      this.$router.push({ name: 'example' });
    },

    async fetchPoints() {
      const { data, error } = await supabase.auth.getSession();
      if (error) {
        console.error(error);
        return;
      }

      if (!this.searchQuery.trim()) {
        alert(this.$t('dashboard.errors.emptyContext'));
        return;
      }

      // Check if at least one source is selected
      if (this.selectedSourcesCount === 0) {
        this.$toast.warning(this.$t('validation.sourceRequired'));
        return;
      }

      this.isLoadingPoints = true;
      this.sourceUnavailable = false;

      try {
        const token = data.session?.access_token;

        const response = await axios.post(
          `${API_URL}/copilot/get_points_new`,
          {
            contexte: this.searchQuery,
            selected_sources: this.selectedSourceIds, // ✅ Envoyer les sources sélectionnées
          },
          {
            headers: {
              Authorization: `Bearer ${token}`
            },
            timeout: 160000
          }
        );

        // Check for source unavailable response
        if (response.data.error_type === 'source_unavailable') {
          this.isLoadingPoints = false;
          this.sourceUnavailable = true;
          return;
        }

        this.pointsList = (response.data.points || []).map(point => ({
          text: point.problem,
          originalText: point.problem,
          sources: point.source.split(',').map(s => s.trim()),
          isEditing: false,
          id: this.pointIdCounter++,
          editingHeight: null,
        }));

        this.global_contexte = response.data.global_contexte;
        this.isLoadingPoints = false;

        this.$nextTick(() => {
          this.scrollToBottom();
        });

      } catch (error) {
        console.error('Error fetching points:', error);

        // Gérer les différents codes d'erreur
        if (error.response?.status === 402) {
          this.showPaywall = true;
        } else if (error.response?.status === 401) {
          this.$toast.error(this.$t('errors.unauthorized'));
          this.$router.push('/login');
        } else {
          this.$toast.error(this.$t('dashboard.toasts.fetchPointsError'));
        }

        this.isLoadingPoints = false;
      } finally {
        this.isSubmittingSearch = false;
      }
    },

    deletePoint(index) {
      this.pointsList.splice(index, 1);
    },

    savePoint(index) {
      const point = this.pointsList[index];
      if (point.text.trim() === '') {
        alert(this.$t('dashboard.errors.emptyPoint'));
        return;
      }
      point.isEditing = false;
    },

    startAnalysis() {
      if (this.pointsList.length === 0) {
        alert(this.$t('dashboard.errors.noPointSelected'));
        return;
      }
      this.startActualAnalysis(this.searchQuery, this.pointsList, this.global_contexte);
      this.isSearchActive = false;
      this.pointsList = [];
      this.global_contexte = '';
      this.chatMessages = []; // Reset chat when starting analysis
    },

    async sendRefinementMessage() {
      if (!this.refinementInput.trim()) {
        return;
      }

      const userMessage = this.refinementInput.trim();

      // Clear input and set loading state
      this.refinementInput = '';
      this.isRefining = true;

      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) {
          console.error(error);
          this.$toast.error(this.$t('dashboard.errors.authError'));
          return;
        }

        const token = data.session?.access_token;

        // Prepare current points for API
        const currentPoints = this.pointsList.map(point => ({
          text: point.text,
          problem: point.text,
          source: point.sources ? point.sources.join(', ') : ''
        }));

        const response = await axios.post(
          `${API_URL}/copilot/refine_points`,
          {
            contexte_initial: this.searchQuery,
            current_points: currentPoints,
            user_message: userMessage,
            conversation_history: [] // No conversation history needed
          },
          {
            headers: {
              Authorization: `Bearer ${token}`
            },
            timeout: 160000
          }
        );

        if (response.data.success) {
          // Update points list with refined points
          this.pointsList = (response.data.points || []).map(point => ({
            text: point.problem,
            originalText: point.problem,
            sources: point.source.split(',').map(s => s.trim()),
            isEditing: false,
            id: this.pointIdCounter++,
            editingHeight: null,
          }));

          // Close the refinement input after success
          this.showRefinementInput = false;
        }

      } catch (error) {
        console.error('Error refining points:', error);

        if (error.response?.status === 402) {
          this.showPaywall = true;
        } else if (error.response?.status === 401) {
          this.$toast.error(this.$t('errors.unauthorized'));
        } else {
          this.$toast.error(this.$t('dashboard.toasts.refinePointsError'));
        }
      } finally {
        this.isRefining = false;
      }
    },

    async startActualAnalysis(context, pointList, globalContexte) {
      if (!context.trim()) {
        alert(this.$t('dashboard.errors.provideContext'));
        return;
      }

      // Détection 1ère recherche (historique local vide AVANT l'insert du nouveau dossier).
      const isFirstSearch = (this.dossiers?.length || 0) === 0;

      try {
        const user = await getCurrentUser();
        if (!user) {
          throw new Error('User not authenticated');
        }

        const dossierName = context.length > 30 ?
            context.substring(0, 30) + '...' :
            context;

        const { data: newDossier, error } = await supabase
          .from('dossiers')
          .insert([{
            user_id: user.id,
            name: dossierName,
            contexte: context,
            created_at: new Date().toISOString(),
            is_analysing: 'pending'
          }])
          .select();

        if (error) throw error;
        const dossierId = newDossier[0].id;
        this.$router.push(`/dossier/${dossierId}`);

        getSupabaseToken()
          .then(token => {
            if (!token) {
              alert(this.$t('dashboard.errors.authRetry'));
              return;
            }
            axios.post(
              `${API_URL}/copilot/get_analyse_fiscale`,
              {
                contexte: context,
                global_contexte: globalContexte,
                dossierId: dossierId,
                userId: user.id,
                point_list: pointList,
                visas_list: [],
                mode: "analyse",
                ...(isFirstSearch && { first_search: true }),
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            ).then(() => {
            }).catch(error => {
              console.error("Error starting analysis:", error);
              supabase
                .from('dossiers')
                .update({ is_analysing: 'failed' })
                .eq('id', dossierId)
                .eq('user_id', user.id);
            });
          })
          .catch(error => {
            console.error("Error fetching token:", error);
            this.$toast.error(this.$t('dashboard.toasts.tokenFetchError'));
          });
      } catch (error) {
        console.error('Error creating dossier:', error);
        alert(this.$t('dashboard.errors.createDossier', { message: error.message }));
      }
    },

    scrollToBottom() {
      window.scrollTo(0, document.body.scrollHeight);
    },

    editPoint(index) {
      const prev = this.pointsList.findIndex(p => p.isEditing);
      if (prev !== -1 && prev !== index) {
        this.savePoint(prev);
      }

      const containerEls = this.$refs.pointText;
      const containerEl = Array.isArray(containerEls)
        ? containerEls[index]
        : containerEls;
      if (containerEl) {
        this.pointsList[index].editingHeight = `${containerEl.clientHeight}px`;
      }

      this.$set(this.pointsList[index], 'isEditing', true);

      this.$nextTick(() => {
        const textAreas = this.$refs.editTextArea;
        const ta = Array.isArray(textAreas) ? textAreas[index] : textAreas;
        if (ta) ta.focus();
      });
    },
  }
};
</script>

<style scoped>
/* Ajouter ces styles à la section <style scoped> */

/* Icône d'épinglage */
.pin-icon {
  width: 18px;
  height: 18px;
  cursor: pointer;
  margin-right: 8px;
  opacity: 0.4;
  transition: all 0.2s;
}

.pin-icon:hover {
  opacity: 1;
  transform: scale(1.1);
}

.pin-icon.pinned {
  opacity: 1;
  fill: #6c63ff;
  filter: brightness(0) saturate(100%) invert(43%) sepia(96%) saturate(2574%) hue-rotate(229deg) brightness(101%) contrast(101%);
}

.finder-card.pinned-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 3px;
  border-radius: 12px 12px 0 0;
}

/* Animation pour les cartes épinglées */
@keyframes pinPulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
}

.finder-card.pinned-card:hover {
  animation: pinPulse 0.3s ease;
}

/* Badge "Épinglé" optionnel */
.pinned-badge {
  position: absolute;
  top: 8px;
  left: 8px;
  background-color: #6c63ff;
  color: white;
  font-size: 0.7rem;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 500;
}


.maintenance-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.maintenance-modal {
  background-color: white;
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  text-align: center;
  font-family: 'Roboto', sans-serif;
}

.maintenance-modal h2 {
  color: #333;
  margin-bottom: 20px;
}

.maintenance-modal p {
  color: #555;
  line-height: 1.6;
}

.icon-button {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: none;
  border: none;
  padding: 0;
}

.icon-label {
  font-size: 0.75rem;
  margin-top: 4px;
  color: #625CFB;
  user-select: none;
}

.icon-label-red {
  font-size: 0.75rem;
  margin-top: 4px;
  color: #ff5252;
  user-select: none;
}

.content {
  padding: 20px;
  font-family: 'Roboto', sans-serif;
  background-color: #F5F6FA;
  min-height: 100vh;
}

.search-section {
  place-items: center;
  margin-bottom: 20px;
  margin-top: 20px;
}

.search-title {
  font-size: 20px;
  font-weight: bold;
}

.highlight {
  background: linear-gradient(90deg, #000000 0%, #625CFB 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
}

.main-section {
  width: 80%;
  max-width: 875px;
  margin: 0 auto;
}

.search-bar {
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: 25px;
  box-shadow:
    0 -2px 4px rgba(0, 0, 0, 0.01),
    0 2px 4px rgba(0, 0, 0, 0.1),
    0 6px 12px rgba(0, 0, 0, 0.05);
  padding: 15px 20px;
}

.input-container {
  width: 100%;
}

.search-input:focus {
  outline: none;
  box-shadow: none;
}

.search-input {
  width: 100%;
  min-height: 20px;
  max-height: 300px;
  padding: 10px;
  border: none;
  border-radius: 15px;
  outline: none;
  font-size: 16px;
  resize: none;
  background-color: white;
  overflow-y: auto;
  color: #000000;
}

.search-input::placeholder {
  color: rgba(0, 0, 0, 0.35);
  opacity: 1;
}

.buttons-line {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
}

/* ========== TABLE STYLES FOR SYNTHESE ========== */
.synthese-content table {
  width: 100%;
  border-collapse: collapse;
  margin: 1em 0;
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}


.button-base {
  padding: 8px 25px;
  border-radius: 25px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.outline-button {
  background-color: white;
  color: #6c63ff;
  font-size: 0.8em;
  border: 1px solid #6c63ff;
}

.search-icon {
  margin-left: 8px;
  width: 16px;
  height: 16px;
}

.tech-purple {
  background-color: #6c63ff;
  color: white;
}

.tech-purple:hover{
  background-color: #5a54d1;
}

.outline-button:hover {
  background-color: #ffffff;
  color: #6c63ff;
}

.search-button:hover {
  background-color: #5a54d1;
  color: white;
}

.search-button img {
  margin-left: 5px;
}

.more-icon {
  width: 20px;
  cursor: pointer;
}

.dossier-menu {
  position: absolute;
  top: 2.5rem;
  right: 1rem;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 10001;
  padding: 5px;
  min-width: 160px;
}

.delete-button {
  background: none;
  border: none;
  color: #ff5252;
  padding: 8px 12px;
  text-align: left;
  font-size: 0.8rem;
  cursor: pointer;
  display: block;
  width: 100%;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.delete-button:hover {
  background-color: #ffebee;
}

.spinner-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #6c63ff;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
}

.mini-spinner {
  border: 2px solid #f3f3f3;
  border-top: 2px solid #6c63ff;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  animation: spin 1s linear infinite;
  display: inline-block;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.synthese-container {
  background-color: #E9EFF4;
  padding: 20px 50px 25px;
  border-radius: 8px;
  background: linear-gradient(to right, #E9EFF4, #E2FAFFCC);
  box-shadow:
    0 -2px 4px rgba(0, 0, 0, 0.01),
    0 2px 4px rgba(0, 0, 0, 0.1),
    0 6px 12px rgba(0, 0, 0, 0.05);
}

.synthese-content {
  padding: 20px;
  font-family: "Roboto", sans-serif;
}

.buttons {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}

.enrichir-button {
  background-color: #6c63ff;
  color: white;
  border: none;
  padding: 8px 25px;
  border-radius: 25px;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.enrichir-button:hover {
  background-color: #5a54d1;
}

.back-button {
  background-color: white;
  color: #6c63ff;
  border: 1px solid #6c63ff;
  padding: 8px 25px;
  border-radius: 25px;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.small-button {
  height: 50%;
  width: 13%;
  padding: 4px 8px;
  cursor: pointer;
}

.small-button:hover {
  background-color: #e0e0e0;
}

.point-item {
  margin-bottom: 2em;
}

.points-list {
  list-style-type: none;
  padding: 0;
}

.point-text {
  padding: 1em;
  padding-right: 0;
  display: flex;
  align-items: center;
  border-radius: 16px;
  font-size: 1.1rem;
  background-color: #f8f9fa;
  margin-right: 10px;
  flex: 1;
  cursor: text;
}

.point-display {
  display: flex;
  align-items: center;
  flex: 1;
}

.title-points {
  margin-bottom: 30px;
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

.source-select-message {
  color: #dc3545;
  font-size: 0.9rem;
  margin: 8px 0 0 0;
  flex-basis: 100%;
}

.source-unavailable-message {
  text-align: center;
  padding: 24px;
}
.source-unavailable-message p {
  font-size: 1rem;
  color: #666;
  margin-bottom: 20px;
}

.button-disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

.quota-overlay {
  position: fixed;
  top: 0; left: 0;
  width: 100vw; height: 100vh;
  background: rgba(0,0,0,0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
}

.quota-modal {
  background: white;
  padding: 30px 40px;
  border-radius: 12px;
  text-align: center;
  max-width: 90%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.group-assignment-menu {
  min-width: 150px;
}
.group-assignment-menu button {
  background: none;
  border: none;
  padding: 8px 12px;
  text-align: left;
  font-size: 0.8rem;
  cursor: pointer;
  display: block;
  width: 100%;
  border-radius: 4px;
}
.group-assignment-menu button:hover {
  background-color: #f0f0ff;
}

.menu-item {
  background: none;
  border: none;
  color: #333;
  padding: 8px 12px;
  text-align: left;
  font-size: 0.8rem;
  cursor: pointer;
  display: block;
  width: 100%;
  border-radius: 4px;
}
.menu-item:hover {
  background-color: #f5f5f5;
}

.dossier-search-container {
  position: relative;
  display: flex;
  align-items: center;
}
.dossier-search-input {
  width: 250px;
  height: 36px;
  border-radius: 18px;
  border: 1px solid #dcdfe6;
  padding-left: 35px;
  padding-right: 15px;
  font-size: 0.85rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.dossier-search-input:focus {
  outline: none;
  border-color: #6c63ff;
  box-shadow: 0 0 0 2px rgba(108, 99, 255, 0.2);
}
.search-icon-grey {
  position: absolute;
  left: 12px;
  color: #a8aebf;
  pointer-events: none;
}
.no-results-message {
  color: #6c757d;
  font-size: 0.9rem;
}

.search-loading-indicator {
  position: absolute;
  right: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Spinner violet dans la barre de recherche des dossiers (fond blanc) */
.search-loading-indicator .mini-spinner {
  border: 2px solid #f3f3f3;
  border-top-color: #6c63ff;
}

/* ========== FINDER LAYOUT ========== */
.finder-container {
  display: flex;
  gap: 0;
  background-color: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow:
    0 -2px 4px rgba(0, 0, 0, 0.01),
    0 2px 4px rgba(0, 0, 0, 0.1),
    0 6px 12px rgba(0, 0, 0, 0.05);
  min-height: 500px;
}

.finder-sidebar {
  width: 250px;
  background: linear-gradient(to bottom, #f8f9fa, #ffffff);
  border-right: 1px solid #e0e4e8;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

.sidebar-header {
  padding: 20px;
  border-bottom: 1px solid #e0e4e8;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.sidebar-header h6 {
  margin: 0;
  font-weight: 600;
  font-size: 0.9rem;
  color: #333;
}

.btn-add-group {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background-color: #6c63ff;
  color: white;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.btn-add-group:hover {
  background-color: #5a54d1;
}

.sidebar-list {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
}

.sidebar-item {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  margin-bottom: 4px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;
  position: relative;
  font-size: 0.85rem;
}

.sidebar-item:hover {
  background-color: rgba(108, 99, 255, 0.08);
}

.sidebar-item.active {
  background-color: rgba(108, 99, 255, 0.15);
  font-weight: 500;
}

.sidebar-icon {
  margin-right: 10px;
  color: #6c63ff;
  flex-shrink: 0;
}

.sidebar-item > span:first-of-type {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-count {
  margin-left: 8px;
  font-size: 0.75rem;
  color: #888;
  background-color: #f0f0f0;
  padding: 2px 8px;
  border-radius: 10px;
  flex-shrink: 0;
}

.sidebar-more-icon {
  width: 16px;
  margin-left: 8px;
  opacity: 0;
  transition: opacity 0.2s;
  flex-shrink: 0;
}

.sidebar-item:hover .sidebar-more-icon {
  opacity: 1;
}

.finder-main {
  flex: 1;
  background-color: #E9EFF4;
  background: linear-gradient(to right, #E9EFF4, #E2FAFFCC);
  display: flex;
  flex-direction: column;
  padding: 20px;
  overflow-y: auto;
  box-shadow:
    0 -2px 4px rgba(0, 0, 0, 0.01),
    0 2px 4px rgba(0, 0, 0, 0.1),
    0 6px 12px rgba(0, 0, 0, 0.05);
}

.finder-header {
  margin-bottom: 20px;
}

.finder-header .dossier-search-container {
  width: 100%;
  max-width: 400px;
}

.finder-header .dossier-search-input {
  width: 100%;
}

.finder-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
  flex: 1;
  align-content: start; /* Add this line */
  align-items: start; /* Add this line */
}

.finder-card {
  background-color: white;
  border: 1px solid #e0e4e8;
  border-radius: 12px;
  padding: 15px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  position: relative;
  min-height: 100px;
  max-height: 150px; /* Add this line to set a maximum height */
  height: fit-content; /* Add this line */
  display: flex;
  flex-direction: column;
}

.finder-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(108, 99, 255, 0.3);
}

/* Élever la carte quand son menu est ouvert pour résoudre le contexte de stacking */
.finder-card:has(.dossier-menu) {
  z-index: 10002;
}

/* ============================================
   REFINEMENT CHAT STYLES
   ============================================ */

.refinement-chat-container {
  display: none;
  margin-top: 2rem;
  background: linear-gradient(135deg, rgba(108, 99, 255, 0.05), rgba(90, 82, 213, 0.05));
  border-radius: 16px;
  padding: 2rem;
  border: 2px solid rgba(108, 99, 255, 0.1);
  box-shadow: 0 4px 20px rgba(108, 99, 255, 0.1);
  transition: all 0.3s ease;
}

.refinement-chat-container:hover {
  box-shadow: 0 6px 28px rgba(108, 99, 255, 0.15);
  border-color: rgba(108, 99, 255, 0.2);
}

.refinement-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  margin-bottom: 1rem;
  font-size: 1rem;
  color: #6c63ff;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(108, 99, 255, 0.08);
}

.chat-header {
  margin-bottom: 1.5rem;
  text-align: center;
}

.chat-header h5 {
  margin: 0 0 0.5rem 0;
  font-size: 1.15rem;
  font-weight: 600;
  color: #2d3748;
  background: linear-gradient(135deg, #6c63ff, #5a52d5);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.chat-subtitle {
  margin: 0;
  font-size: 0.85rem;
  color: #718096;
  font-style: italic;
  font-weight: 400;
}

.refinement-toggle-container {
  display: flex;
  justify-content: center;
  margin-bottom: 0;
}

.refinement-toggle-button {
  display: inline-flex;
  align-items: center;
  padding: 0.7rem 1.5rem;
  background: white;
  border: 2px solid rgba(108, 99, 255, 0.2);
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 500;
  color: #6c63ff;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(108, 99, 255, 0.1);
}

.refinement-toggle-button:hover {
  background: linear-gradient(135deg, rgba(108, 99, 255, 0.05), rgba(90, 82, 213, 0.05));
  border-color: rgba(108, 99, 255, 0.4);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(108, 99, 255, 0.2);
}

.refinement-toggle-button:active {
  transform: translateY(0);
}

.refinement-input-section {
  /* Uses existing Dashboard styles from input-container and buttons-line */
}

.chat-input-container {
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  background: white;
  padding: 1rem;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
}

.chat-input-container:focus-within {
  box-shadow: 0 4px 16px rgba(108, 99, 255, 0.15);
}

.chat-input {
  flex: 1;
  padding: 0.65rem 1rem;
  border: 2px solid #e8e9ef;
  border-radius: 10px;
  font-size: 0.9rem;
  resize: none;
  min-height: 42px;
  font-family: inherit;
  transition: all 0.3s ease;
  background: #f8f9fa;
}

.chat-input:focus {
  outline: none;
  border-color: #6c63ff;
  background: white;
  box-shadow: 0 0 0 3px rgba(108, 99, 255, 0.1);
}

.chat-input:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
  opacity: 0.6;
}

.chat-input::placeholder {
  color: #a0aec0;
  font-style: italic;
}

.chat-send-button {
  padding: 0.65rem 1.5rem;
  background: linear-gradient(135deg, #6c63ff, #5a52d5);
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  min-width: 110px;
  height: fit-content;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(108, 99, 255, 0.25);
  letter-spacing: 0.3px;
}

.chat-send-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(108, 99, 255, 0.4);
  background: linear-gradient(135deg, #5a52d5, #4a42c5);
}

.chat-send-button:active:not(:disabled) {
  transform: translateY(0);
}

.chat-send-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  box-shadow: none;
}

/* Mini spinner for chat loading */
.mini-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .refinement-chat-container {
    padding: 1.5rem;
    margin-top: 1.5rem;
  }

  .chat-input-container {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }

  .chat-send-button {
    width: 100%;
  }

  .chat-header h5 {
    font-size: 1rem;
  }
}


.finder-card-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.finder-card-body {
  flex: 1;
  font-size: 0.9rem;
  font-weight: 500;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

/* Remplacez les styles précédents par ceux-ci */

.finder-card-header-left {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.dossier-date {
  font-size: 0.7rem;
  color: #6c63ff;
  background: linear-gradient(135deg, rgba(108, 99, 255, 0.08), rgba(108, 99, 255, 0.12));
  padding: 3px 10px;
  border-radius: 12px;
  white-space: nowrap;
  font-weight: 500;
  border: 1px solid rgba(108, 99, 255, 0.15);
  transition: all 0.2s ease;
}

.finder-card:hover .dossier-date {
  background: linear-gradient(135deg, rgba(108, 99, 255, 0.15), rgba(108, 99, 255, 0.2));
  border-color: rgba(108, 99, 255, 0.3);
  transform: scale(1.02);
}

.dossier-mode-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.65rem;
  color: #6c63ff;
  background-color: transparent;
  padding: 2px 8px;
  border-radius: 10px;
  white-space: nowrap;
  font-weight: 500;
  border: 1px solid rgba(108, 99, 255, 0.25);
  margin-left: 6px;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  transition: all 0.2s ease;
}

.dossier-mode-tag-icon {
  flex-shrink: 0;
}

.finder-card:hover .dossier-mode-tag {
  background-color: rgba(108, 99, 255, 0.08);
  border-color: rgba(108, 99, 255, 0.4);
}

/* Ajuster l'icône d'épinglage pour mieux s'aligner */
.pin-icon {
  width: 18px;
  height: 18px;
  cursor: pointer;
  flex-shrink: 0;
  opacity: 0.4;
  transition: all 0.2s;
}

.pin-icon:hover {
  opacity: 1;
  transform: scale(1.1);
}

.pin-icon.pinned {
  opacity: 1;
  stroke: #6c63ff;
}

/* Ajuster le header pour utiliser flex */
.finder-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  gap: 8px;
}

/* Style alternatif plus discret si vous préférez */
.dossier-date-alt {
  font-size: 0.7rem;
  color: #6c63ff;
  background-color: rgba(108, 99, 255, 0.1);
  padding: 2px 8px;
  border-radius: 10px;
  white-space: nowrap;
  font-weight: 500;
  transition: all 0.2s ease;
}

.finder-card:hover .dossier-date-alt {
  background-color: rgba(108, 99, 255, 0.2);
  transform: translateY(-1px);
}

/* Styles pour l'icône d'information */
.finder-card-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-icon {
  cursor: pointer;
  color: #6c63ff;
  opacity: 0.6;
  transition: all 0.2s;
}

.info-icon:hover {
  opacity: 1;
  transform: scale(1.1);
}

/* Styles pour le modal d'information */
.info-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.info-modal {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.info-modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid #e0e4e8;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.info-modal-header h3 {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
}

.info-modal-close {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
  color: #666;
}

.info-modal-close:hover {
  background-color: #f5f5f5;
  color: #333;
}

.info-modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.info-modal-body p {
  margin: 0;
  line-height: 1.6;
  color: #555;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.info-modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #e0e4e8;
  display: flex;
  justify-content: flex-end;
}

.info-modal-footer .btn {
  padding: 10px 24px;
  font-size: 0.9rem;
}

.agent-button {
  background-color: #ffffff;
  color: #7c3aed;
  border: 1.5px solid #7c3aed;
  position: relative;
  font-weight: 600;
  transition: all 0.2s ease;
}

.agent-button:hover:not(:disabled) {
  background-color: #f5f3ff;
  border-color: #6d28d9;
  color: #6d28d9;
  box-shadow: 0 2px 10px rgba(124, 58, 237, 0.2);
}

.agent-button:disabled {
  background-color: #f9f9f9;
  color: #c4b5fd;
  border-color: #ddd6fe;
  cursor: not-allowed;
}

.flash-button {
  background-color: #ffffff;
  color: #333;
  border: 1.5px solid #e0e4e8;
  position: relative;
  font-weight: 500;
  transition: all 0.2s ease;
}

.flash-button:hover:not(:disabled) {
  border-color: #6c63ff;
  background-color: #f8f7ff;
  color: #6c63ff;
  box-shadow: 0 2px 8px rgba(108, 99, 255, 0.15);
}

.flash-button:disabled {
  background-color: #f5f5f5;
  color: #999;
  border-color: #e0e4e8;
  cursor: not-allowed;
}

/* Optionnel : Animation subtile sur l'icône éclair */
.flash-button:hover:not(:disabled)::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  background: radial-gradient(circle, rgba(108, 99, 255, 0.1) 0%, transparent 70%);
  opacity: 0;
  animation: flashPulse 1.5s ease-in-out infinite;
}

@keyframes flashPulse {
  0%, 100% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.8);
  }
  50% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
}

.tableau-button {
  background-color: #ffffff;
  color: #333;
  border: 1.5px solid #e0e4e8;
  font-weight: 500;
  transition: all 0.2s ease;
}

.tableau-button:hover:not(:disabled) {
  border-color: #10b981;
  background-color: #f0fdf4;
  color: #059669;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.15);
}

.tableau-button:disabled {
  background-color: #f5f5f5;
  color: #999;
  border-color: #e0e4e8;
  cursor: not-allowed;
}

/* Mode selector dropdown */
.mode-selector-container {
  position: relative;
  margin-right: 8px;
}

.mode-selector-btn {
  background-color: #ffffff;
  color: #374151;
  border: 1.5px solid #e0e4e8;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;
}

.mode-selector-btn:hover:not(:disabled) {
  border-color: #6c63ff;
  background-color: #f8f7ff;
  color: #6c63ff;
}

.mode-selector-btn:disabled {
  background-color: #f5f5f5;
  color: #999;
  border-color: #e0e4e8;
  cursor: not-allowed;
}

.mode-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  min-width: 170px;
  z-index: 200;
  overflow: hidden;
}

.mode-dropdown-option {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 10px 14px;
  background: none;
  border: none;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;
}

.mode-dropdown-option:hover:not(:disabled) {
  background-color: #f9fafb;
}

.mode-dropdown-option:disabled {
  color: #aaa;
  cursor: not-allowed;
}

.mode-dropdown-option + .mode-dropdown-option {
  border-top: 1px solid #f3f4f6;
}

.mode-dropdown-option--active {
  background-color: #f5f3ff;
  color: #6c63ff;
}

.mode-dropdown-option--active:hover:not(:disabled) {
  background-color: #ede9fe;
}

.mode-selector-btn.mode-active {
  border-color: #6c63ff;
  color: #6c63ff;
  background-color: #f5f3ff;
}

/* Workspace selector */
.workspace-selector-container {
  position: relative;
  display: inline-block;
}
.workspace-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  min-width: 240px;
  max-width: 320px;
  z-index: 200;
  overflow: hidden;
}
.workspace-dropdown-header {
  padding: 10px 14px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  border-bottom: 1px solid #f3f4f6;
  background: #fafafa;
}
.workspace-dropdown-body {
  max-height: 260px;
  overflow-y: auto;
}
.workspace-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 10px 14px;
  background: none;
  border: none;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;
}
.workspace-item:hover:not(:disabled) { background: #f9fafb; }
.workspace-item:disabled { cursor: not-allowed; }
.workspace-dropdown-body > * + * { border-top: 1px solid #f3f4f6; }
.workspace-row {
  display: flex;
  align-items: center;
}
.workspace-row .workspace-item { flex: 1; min-width: 0; }
.workspace-item__actions {
  display: flex;
  align-items: center;
  gap: 2px;
  padding-right: 8px;
  flex-shrink: 0;
}
.ws-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: #9ca3af;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
}
.ws-action-btn:hover { background: #f5f3ff; color: #6c63ff; }
.ws-more-icon {
  width: 16px;
  height: 16px;
  display: block;
  transition: transform 0.12s;
}
.ws-action-btn:hover .ws-more-icon { transform: scale(1.1); }
.workspace-item--active { background: #f5f3ff; color: #6c63ff; font-weight: 500; }
.workspace-item--active:hover { background: #ede9fe; }
.workspace-item--pending { color: #6b7280; }
.workspace-item--pending .workspace-item__name { font-style: italic; }
.workspace-item--failed { color: #b91c1c; }
.workspace-item__main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.workspace-item__name { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.workspace-item__spinner {
  color: #6c63ff;
  display: inline-flex;
  animation: ws-spin 1s linear infinite;
  flex-shrink: 0;
}
.workspace-item__failed {
  color: #b91c1c;
  font-weight: bold;
  flex-shrink: 0;
}
@keyframes ws-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.workspace-empty {
  padding: 14px;
  text-align: center;
  color: #9ca3af;
  font-size: 12px;
}
.workspace-dropdown-footer {
  padding: 8px;
  border-top: 1px solid #f3f4f6;
  background: #fafafa;
  display: flex;
  gap: 6px;
}
.workspace-item--more {
  color: #6c63ff;
  font-weight: 500;
}
.workspace-item--more:hover { background: #f5f3ff; }
.workspace-more-count {
  flex-shrink: 0;
  min-width: 18px;
  padding: 1px 6px;
  background: #f5f3ff;
  color: #6c63ff;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
}
.workspace-create-btn {
  flex: 1;
  padding: 8px 10px;
  background: linear-gradient(135deg, #6c63ff, #5a52d5);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
}
.workspace-create-btn:hover { filter: brightness(1.06); }

.tableau-modal {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  max-width: 540px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
}

.tableau-modal-body {
  padding: 16px 0;
}

.tableau-modal-hint {
  font-size: 13px;
  color: #666;
  margin-bottom: 16px;
}

.tableau-sq-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 10px;
}

.tableau-sq-label {
  font-size: 13px;
  font-weight: 600;
  color: #555;
  min-width: 20px;
  padding-top: 8px;
}

.tableau-sq-input {
  flex: 1;
  min-width: 0;
  min-height: 2.5em;
  padding: 8px 12px;
  border: 1.5px solid #e0e4e8;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-wrap: break-word;
  resize: none;
  overflow-y: auto;
  transition: border-color 0.2s;
}

.tableau-sq-input:focus {
  outline: none;
  border-color: #6c63ff;
}

.tableau-sq-remove {
  background: none;
  border: none;
  color: #999;
  font-size: 18px;
  cursor: pointer;
  padding: 8px 4px 0;
  line-height: 1;
}

.tableau-sq-remove:hover {
  color: #ef4444;
}

.tableau-loading-box {
  padding: 24px 16px;
  text-align: center;
}

.tableau-loading-spinner {
  width: 32px;
  height: 32px;
  margin: 0 auto 12px;
  border: 3px solid #e0e4e8;
  border-top-color: #6c63ff;
  border-radius: 50%;
  animation: tableau-spin 0.8s linear infinite;
}

@keyframes tableau-spin {
  to { transform: rotate(360deg); }
}

/* ===== Improve question (baguette magique) ===== */

/* Bouton baguette : violet du sélecteur de modes, se remplit au hover */
.improve-button {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid #6c63ff;
  background-color: #f5f3ff;
  color: #6c63ff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.improve-button:hover:not(:disabled) {
  background-color: #6c63ff;
  color: #ffffff;
  box-shadow: 0 2px 8px rgba(108, 99, 255, 0.4);
}

.improve-button:disabled {
  border-color: #e0e4e8;
  background-color: #ffffff;
  color: #c2c7cf;
  cursor: not-allowed;
}

/* Overlay + coque (même langage que la modal Transformer) */
.improve-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(26, 26, 46, 0.45);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
}

.improve-modal {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: 560px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.improve-modal-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 24px 16px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.improve-modal-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #eef2ff, #e0e7ff);
  color: #6c63ff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.improve-modal-title {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: #1a1a2e;
}

.improve-modal-subtitle {
  margin: 2px 0 0;
  font-size: 13px;
  color: #6b7280;
}

.improve-modal-close {
  margin-left: auto;
  background: none;
  border: none;
  padding: 6px;
  border-radius: 8px;
  cursor: pointer;
  color: #9ca3af;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.improve-modal-close:hover {
  background: #f3f4f6;
  color: #374151;
}

/* Corps : seule zone scrollable (header et footer restent visibles) */
.improve-modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
}

.improve-waiting {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 48px 20px;
}

.improve-waiting-dots {
  display: flex;
  gap: 8px;
}

.improve-waiting-dots span {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #6c63ff;
  animation: improveBounce 1.4s ease-in-out infinite both;
}

.improve-waiting-dots span:nth-child(1) { animation-delay: 0s; }
.improve-waiting-dots span:nth-child(2) { animation-delay: 0.2s; }
.improve-waiting-dots span:nth-child(3) { animation-delay: 0.4s; }

@keyframes improveBounce {
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.5; }
  40% { transform: scale(1); opacity: 1; }
}

.improve-waiting-text {
  margin: 0;
  font-size: 14px;
  color: #6b7280;
}

/* Cartes question : le liseré gauche passe au violet quand on a répondu */
.improve-q-card {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-left: 3px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px 16px;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.improve-q-card + .improve-q-card {
  margin-top: 12px;
}

.improve-q-card:focus-within {
  border-color: #c7c2ff;
  border-left-color: #6c63ff;
  background: #fff;
}

.improve-q-card--answered {
  border-left-color: #6c63ff;
  background: #fff;
}

.improve-q-text {
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1a1a2e;
  line-height: 1.45;
}

.improve-a-input {
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  resize: none;
  font-size: 14px;
  line-height: 1.5;
  color: #1f2937;
  padding: 0;
  min-height: 24px;
  max-height: 160px;
  overflow-y: auto;
}

.improve-a-input::placeholder {
  color: #9ca3af;
}

/* Résultat */
.improve-original {
  margin-bottom: 16px;
}

.improve-block-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9ca3af;
  margin-bottom: 6px;
}

.improve-block-label--accent {
  color: #6c63ff;
}

.improve-original-text {
  margin: 0;
  font-size: 13px;
  color: #6b7280;
  background: #f9fafb;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  padding: 10px 14px;
  white-space: pre-wrap;
  max-height: 120px;
  overflow-y: auto;
}

.improve-result-input {
  width: 100%;
  border: 1.5px solid #ded9ff;
  border-radius: 12px;
  background: #fdfdff;
  padding: 14px 16px;
  font-size: 14px;
  line-height: 1.55;
  color: #1f2937;
  resize: none;
  min-height: 140px;
  max-height: 320px;
  overflow-y: auto;
  transition: border-color 0.2s;
}

.improve-result-input:focus {
  outline: none;
  border-color: #6c63ff;
  box-shadow: 0 0 0 3px rgba(108, 99, 255, 0.12);
}

.improve-reveal {
  animation: improveReveal 0.9s ease-out forwards;
}

@keyframes improveReveal {
  0% { opacity: 0; clip-path: inset(0 0 100% 0); }
  100% { opacity: 1; clip-path: inset(0 0 0 0); }
}

/* Footer fixe */
.improve-modal-footer {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 24px;
  border-top: 1px solid #f0f0f0;
  flex-shrink: 0;
  background: #fff;
}

.improve-footer-note {
  font-size: 12px;
  color: #9ca3af;
}

.improve-footer-actions {
  display: flex;
  gap: 8px;
  margin-left: auto;
}

.improve-btn-ghost {
  padding: 9px 16px;
  border-radius: 9999px;
  border: 1px solid #e5e7eb;
  background: #fff;
  font-size: 13px;
  font-weight: 500;
  color: #4b5563;
  cursor: pointer;
  transition: all 0.15s ease;
}

.improve-btn-ghost:hover {
  background: #f3f4f6;
}

.improve-btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 18px;
  border-radius: 9999px;
  border: none;
  background: #6c63ff;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(108, 99, 255, 0.35);
  transition: all 0.2s ease;
}

.improve-btn-primary:hover:not(:disabled) {
  background: #5a54d1;
  box-shadow: 0 4px 12px rgba(108, 99, 255, 0.45);
  transform: translateY(-1px);
}

.improve-btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Transitions d'ouverture (même langage que Transformer) */
.improve-fade-enter-active,
.improve-fade-leave-active {
  transition: opacity 0.25s ease;
}

.improve-fade-enter-active .improve-modal,
.improve-fade-leave-active .improve-modal {
  transition: transform 0.25s ease, opacity 0.25s ease;
}

.improve-fade-enter,
.improve-fade-leave-to {
  opacity: 0;
}

.improve-fade-enter .improve-modal,
.improve-fade-leave-to .improve-modal {
  transform: translateY(16px) scale(0.97);
  opacity: 0;
}

@media (max-width: 640px) {
  .improve-modal-overlay {
    padding: 12px;
    align-items: flex-end;
  }
  .improve-modal {
    max-width: 100%;
    max-height: 90vh;
    border-radius: 16px 16px 0 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .improve-reveal,
  .improve-waiting-dots span {
    animation: none;
  }
}

.tableau-add-btn {
  font-size: 12px;
  color: #6c63ff;
  background: none;
  border: 1.5px dashed #d0cdf5;
  border-radius: 8px;
  padding: 6px 14px;
  cursor: pointer;
  margin-top: 4px;
  transition: all 0.2s;
}

.tableau-add-btn:hover {
  background: #f8f7ff;
  border-color: #6c63ff;
}

.tableau-refine-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
}

.tableau-refine-hint {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 8px;
}

.tableau-refine-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.tableau-refine-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #e0e4e8;
  border-radius: 8px;
  font-size: 13px;
}

.tableau-refine-btn {
  font-size: 12px;
  padding: 8px 14px;
  border-radius: 8px;
  background: #f0f2ff;
  border: 1px solid #6c63ff;
  color: #6c63ff;
  cursor: pointer;
  white-space: nowrap;
}

.tableau-refine-btn:hover:not(:disabled) {
  background: #e8e6ff;
}

.tableau-refine-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ========== MICROPHONE BUTTON STYLES ========== */
.action-buttons-group {
  display: flex;
  align-items: center;
  gap: 0;
}

.mic-button {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid #e0e4e8;
  background-color: #ffffff;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-right: 0;
  flex-shrink: 0;
}

.mic-button:hover:not(:disabled) {
  border-color: #6c63ff;
  color: #6c63ff;
  background-color: #f8f7ff;
}

.mic-button.recording {
  border-color: #ff4444;
  background-color: #ff4444;
  color: white;
  animation: pulse-recording 1.5s ease-in-out infinite;
}

.mic-button.transcribing {
  border-color: #6c63ff;
  background-color: #f8f7ff;
  cursor: wait;
}

.mic-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

@keyframes pulse-recording {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 68, 68, 0.4);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(255, 68, 68, 0);
  }
}

.mic-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid #e0e4e8;
  border-top: 2px solid #6c63ff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* ========== SOURCES DROPDOWN STYLES ========== */
.left-buttons-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sources-dropdown-container {
  position: relative;
}

/* ========== BOUTON « + » (popover Sources / Rédiger) ========== */
.plus-button {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid #e0e4e8;
  background-color: #ffffff;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.plus-button:hover:not(:disabled),
.plus-button.active {
  border-color: #6c63ff;
  color: #6c63ff;
  background-color: #f8f7ff;
}

.plus-menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  min-width: 170px;
  z-index: 200;
  overflow: hidden;
  animation: dropdownFadeIn 0.2s ease;
}

.plus-menu-option {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 14px;
  background: none;
  border: none;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;
}

.plus-menu-option:hover:not(:disabled) {
  background-color: #f8f7ff;
  color: #6c63ff;
}

.plus-menu-option:disabled {
  color: #aaa;
  cursor: not-allowed;
}

.plus-menu-option + .plus-menu-option {
  border-top: 1px solid #f3f4f6;
}

/* ========== CHIP RÉDACTEUR (modèle sélectionné) ========== */
.redacteur-chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}

.redacteur-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  max-width: 100%;
  padding: 5px 8px 5px 12px;
  border-radius: 999px;
  background: #5b56f0;
  border: 1px solid #5b56f0;
  color: #ffffff;
  font-size: 12.5px;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(91, 86, 240, 0.35);
}

.redacteur-chip-name {
  min-width: 0;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.redacteur-chip-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.22);
  color: #ffffff;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;
}

.redacteur-chip-remove:hover {
  background: rgba(255, 255, 255, 0.4);
}

.sources-button {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 20px;
  border: 2px solid #e0e4e8;
  background-color: #ffffff;
  color: #666;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.sources-button:hover:not(:disabled) {
  border-color: #6c63ff;
  color: #6c63ff;
  background-color: #f8f7ff;
}

.sources-button.active {
  border-color: #6c63ff;
  color: #6c63ff;
  background-color: #f8f7ff;
}

.sources-button.has-selection {
  border-color: #6c63ff;
  color: #6c63ff;
}

.sources-badge {
  background-color: #6c63ff;
  color: white;
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 10px;
  font-weight: 600;
  min-width: 18px;
  text-align: center;
}

.sources-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 8px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 280px;
  max-height: 400px;
  display: flex;
  flex-direction: column;
  animation: dropdownFadeIn 0.2s ease;
}

@keyframes dropdownFadeIn {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.sources-dropdown-header {
  padding: 12px 16px;
  border-bottom: 1px solid #e0e4e8;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  font-size: 0.9rem;
  color: #333;
}

.sources-actions {
  display: flex;
  gap: 8px;
}

.sources-action-btn {
  background: none;
  border: none;
  color: #6c63ff;
  font-size: 0.75rem;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: background-color 0.2s;
}

.sources-action-btn:hover {
  background-color: #f8f7ff;
}

.sources-dropdown-body {
  padding: 8px 0;
  overflow-y: auto;
  max-height: 280px;
}

.sources-category {
  padding: 0 8px;
  margin-bottom: 8px;
}

.sources-category:last-child {
  margin-bottom: 0;
}

.sources-category-title {
  font-size: 0.7rem;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  padding: 8px 8px 4px;
  letter-spacing: 0.5px;
}

.source-item {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  padding: 8px 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.source-item:hover {
  background-color: #f8f7ff;
}

.source-checkbox {
  width: 16px;
  height: 16px;
  min-width: 16px;
  flex-shrink: 0;
  accent-color: #6c63ff;
  cursor: pointer;
}

.source-label {
  font-size: 0.85rem;
  color: #333;
  flex: 1;
  text-align: left;
}

.sources-dropdown-footer {
  padding: 12px 16px;
  border-top: 1px solid #e0e4e8;
  display: flex;
  justify-content: flex-end;
}

</style>
